import { i18n } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { CampaignPreflightPanel } from '../components/campaign-preflight-panel.js';
import { CampaignRefineDrawer, type RefineOpener } from '../components/campaign-refine-drawer.js';
import { CollapseToggle, CollapsibleBlock } from '../components/collapsible.js';
import { ConfirmDialog } from '../components/confirm-dialog.js';
import { GoogleAdPreview, hostnameOf } from '../components/google-ad-preview.js';
import { Mag } from '../components/mag.js';
import { MagMessageBody } from '../components/mag-message-body.js';
import { magToast } from '../components/toaster.js';
import { Tooltip } from '../components/tooltip.js';
import { TopNav } from '../components/top-nav.js';
import { trackConversion } from '../lib/analytics.js';
import {
  type AnalyticsEventDeliverable,
  type AnalyticsTrackerContext,
  type CampaignDetail,
  type CampaignPlan,
  type CampaignPlanningLog,
  type DeliverableServingStatus,
  deleteCampaign,
  fetchMetaPaidAdMetricsHistory,
  type GoogleSearchAdDeliverable,
  getCampaignPreflight,
  type InstagramPostDeliverable,
  launchCampaign,
  type MetaPaidAdDeliverable,
  type PaidAdHookDeliverable,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { draftToPlanView } from '../lib/draft-to-plan.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import {
  formatDate,
  formatDateTime,
  formatNumber,
  formatRelativeTimeCompact,
  formatTime,
} from '../lib/format.js';
import { analyticsQuery, campaignQuery, meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';
import {
  deliverableServingState,
  type ServingState,
  servingReasonPhrase,
} from '../lib/serving-state.js';

// REVIEW screen — new-campaign handoff §4.4. Mag's pitch at top, proposal
// grid below (audience, budget, creative, optimizing-for, timeline), then
// THE ONE ASK — the only decision the founder has to make. Editing of the
// individual panels (audience picker, budget split adjuster, creative
// regen) is intentionally out of scope this session (handoff §8.5).

/** The `t` that useLingui() hands back, so the helpers that run
 *  outside a component can take it as an argument. */
type T = ReturnType<typeof useLingui>['t'];

export const Route = createFileRoute('/brand/$slug/campaigns/new/review/$id')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const campaign = await queryClient.ensureQueryData(campaignQuery(params.id));
    if (!campaign) throw redirect({ to: '/brands' });
    if (campaign.status === 'drafting') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/drafting/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    if (campaign.status === 'launched') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/launched/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    if (!campaign.draft) {
      throw redirect({
        to: '/brand/$slug/campaigns',
        params: { slug: params.slug },
      });
    }
    // organic_meta has its own per-slot surface on the campaign-detail
    // page (OrganicMetaPlanSection — drafts, renders, and publishes
    // each slot independently). The unified review/launch flow assumes
    // a plan-shaped draft, so route organic_meta straight to the
    // detail page instead of crashing in draftToPlanView.
    if (campaign.kind === 'organic_meta') {
      throw redirect({
        to: '/brand/$slug/campaigns/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    return { me, campaign };
  },
  loader: ({ context }) => context as { me: unknown; campaign: CampaignDetail },
  component: ReviewPage,
});

function ReviewPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { campaign } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const draft = campaign.draft;
  const [working, setWorking] = useState<string | null>(null);
  // Pixel gate (s248 / [12.2L-22]) — block launch when no analytics
  // tracker is firing events yet. Founder can either install or
  // explicitly skip on the modal; skipping arms `pixelSkipped` so the
  // modal doesn't re-fire if the launch errors and they retry.
  const { data: analytics } = useQuery(analyticsQuery(campaign.brandId));
  const hasPixel = (analytics?.projects ?? []).some((p) => p.firstEventAt !== null);
  const [pixelGateChoice, setPixelGateChoice] = useState<null | { choice: string }>(null);
  const [pixelSkipped, setPixelSkipped] = useState(false);
  // [s375] Non-null while the refinement drawer is open; the value is
  // the panel-scoped opener so mag starts on the right subject.
  const [refineOpener, setRefineOpener] = useState<RefineOpener | null>(null);
  // [spec a49b8039] Discard. Two flags, not one: `confirmingDiscard` owns
  // the dialog, `discarding` owns the in-flight delete. ConfirmDialog reads
  // the second as `busy` to disable both buttons and block Escape/backdrop
  // close mid-request, which is what keeps a double click from firing a
  // second DELETE that 404s into an error toast.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  // organic_meta drafts don't have a top-level `name` — they carry
  // a summary instead. Narrow via the legacy shape; fall back to
  // the campaign-row name (mag picks a name during intake) so the
  // title still reads cleanly.
  const draftName = (draft as { name?: string } | null)?.name ?? null;
  useBrandTitle(slug, [draftName ? `${draftName} · review` : (campaign.name ?? 'review')]);

  if (!draft) {
    return null; // beforeLoad already redirected
  }

  const handleLaunch = async (choice: string) => {
    // Pixel gate: if the brand has no live tracker and the founder
    // hasn't already chosen "launch anyway" on the modal, intercept
    // the launch and prompt. The modal owns the next step.
    if (!hasPixel && !pixelSkipped) {
      setPixelGateChoice({ choice });
      return;
    }
    setWorking(choice);
    try {
      await launchCampaign(campaign.id, choice);
      // Refresh the campaign cache BEFORE navigating. The launched
      // route's beforeLoad reads campaign.status from cache — without
      // this refresh it sees the stale 'ready' value, redirects back
      // to review, review's beforeLoad refetches and sees 'launched',
      // redirects to launched, and tanstack-router catches the loop
      // and crashes into the error boundary.
      // Launched for real as of the line above — launchCampaign threw on
      // every failure path, so this is the confirmed branch rather than the
      // button press. Reported before the cache refresh so a slow refetch
      // can't sit between the launch and the event.
      trackConversion('campaign_launched', {
        campaign_id: campaign.id,
        // Which surface mag was told to launch into (the review screen's
        // launch choice), and the campaign shape behind it.
        launch_choice: choice,
        kind: campaign.kind,
        // The spend the founder just committed to per day. This is the only
        // number on this event worth optimising toward, so it is the one
        // that goes in `value` — a launch with no budget block (an organic
        // or analytics-only plan) reports null rather than a fake zero.
        value: draftToPlanView(draft).budget?.dailyUsd ?? null,
        // Whether a tracker was already firing when they launched. A launch
        // past the skipped pixel gate is a campaign we will not be able to
        // read the landing-page half of, and that is worth being able to
        // segment on later.
        had_pixel: hasPixel,
      });
      await refreshQueries(queryClient, ['campaign', campaign.id], ['campaigns']);
      await router.invalidate();
      navigate({
        to: '/brand/$slug/campaigns/new/launched/$id',
        params: { slug, id: campaign.id },
      });
    } catch (err) {
      magToast.error("mag couldn't launch.", founderErrorCopy(err, 'try again.'));
      setWorking(null);
    }
  };

  // [spec a49b8039] Discard the plan. Mirrors the campaigns-list flow
  // (brand.$slug.campaigns.index.tsx) rather than inventing a second shape:
  // same endpoint, same refresh pair, same toast register.
  //
  // NAVIGATING AWAY IS REQUIRED, NOT COSMETIC. The row this page renders no
  // longer exists, and the route's own beforeLoad would 404 on any re-read —
  // so the invalidate below must be followed by leaving, not by staying and
  // hoping nothing reads the campaign again.
  const handleDiscard = async () => {
    setDiscarding(true);
    try {
      await deleteCampaign(campaign.id);
      // Mutation-refresh rule: refetch, don't just invalidate — ensureQueryData
      // reads in beforeLoad don't subscribe, so an invalidate alone leaves the
      // list route reading a cached row that is gone.
      await refreshQueries(queryClient, ['campaigns']);
      await router.invalidate();
      magToast.success(t`plan discarded.`, t`it's off your list.`);
      // void, not await, and deliberately so: the campaign is already gone by
      // here, so a navigation failure is not a failed discard. Awaiting inside
      // this try would route it into the catch below and tell the founder
      // "mag couldn't discard that" about something that did happen.
      void navigate({ to: '/brand/$slug/campaigns', params: { slug } });
    } catch (err) {
      magToast.error("mag couldn't discard that.", founderErrorCopy(err, 'try again.'));
      setDiscarding(false);
      setConfirmingDiscard(false);
    }
  };

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active="campaigns" />
      <header className="flex h-12 items-center justify-between border-b border-[var(--line)] bg-[var(--cream)] px-4 sm:px-7">
        <Link
          to="/brand/$slug/campaigns/new/brief"
          params={{ slug }}
          className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
        >
          {'←'} <Trans>back to brief</Trans>
        </Link>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>NEW CAMPAIGN</Trans>
          </span>
          <span className="inline-flex items-center rounded-full bg-[var(--spell)] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]">
            <Trans>STEP 2 OF 2 · REVIEW</Trans>
          </span>
        </div>
      </header>

      <section className="mx-auto max-w-[1040px] px-4 pb-16 pt-8 sm:px-7">
        {/* Plan-mode trail (s1 of plan mode). When the campaign was
            drafted via the multi-agent panel, the planning_log carries
            the synthesis note + per-persona critiques. Render those
            above the standard plan body so the founder sees mag's
            "why this plan" framing before the proposal grid. */}
        {/* Panel critique trail (per-persona concerns + suggestions) is
            intentionally NOT rendered here — it's internal pre-synthesis
            work; the synthesizer already folded the useful pieces in,
            and the raw critique is jargon-heavy debug content the
            founder shouldn't see. Kept in PlanningLog for engineering
            audit / replay; surface via /debug if we ever need to. */}
        {campaign.planningLog ? (
          <div className="mb-6">
            <PanelSynthesisCard log={campaign.planningLog} />
          </div>
        ) : null}
        {/* s182 — one render path. draftToPlanView promotes legacy
            CampaignDraft (creative_ads) and AnalyticsSetupDraft into
            the plan shape; new CampaignPlan drafts pass through
            unchanged. */}
        <PlanReviewBody
          plan={draftToPlanView(draft)}
          campaignId={campaign.id}
          tracker={campaign.analyticsTracker ?? null}
          working={working}
          onChoice={handleLaunch}
          elapsedSeconds={secondsBetween(campaign.createdAt, campaign.readyAt)}
          onRefine={setRefineOpener}
        />

        {/* [spec a49b8039] The way out. Sits BELOW the launch block and is
            styled quiet on purpose — launch is the primary action and
            discard is the escape, so the hierarchy has to read that way at
            a glance. Page-level rather than inside the launch blocks
            because there are two of them (OneAskBlock / PlainLaunchBlock)
            and a founder should not find this in one plan and not another. */}
        <div className="mt-8 flex items-center justify-center gap-2 border-t border-[var(--line)] pt-5">
          <span className="text-[12.5px] text-[var(--ink)]/55">
            <Trans>not what you asked for?</Trans>
          </span>
          <button
            type="button"
            onClick={() => setConfirmingDiscard(true)}
            className="link-quiet text-[12.5px]"
          >
            <Trans>discard this plan</Trans>
          </button>
        </div>
      </section>

      {pixelGateChoice ? (
        <PixelGateModal
          brandSlug={slug}
          onInstallClick={() => {
            setPixelGateChoice(null);
            navigate({ to: '/brand/$slug/analytics', params: { slug } });
          }}
          onLaunchAnyway={() => {
            const pendingChoice = pixelGateChoice.choice;
            setPixelGateChoice(null);
            setPixelSkipped(true);
            // Re-enter handleLaunch — pixelSkipped is now true so it'll
            // sail past the gate and call launchCampaign.
            void handleLaunch(pendingChoice);
          }}
          onDismiss={() => setPixelGateChoice(null)}
        />
      ) : null}

      {/* [spec a49b8039] Irreversible: the row, the founder's brief and
          mag's plan all go. Hence a destructive confirm rather than a bare
          button — the same gate the campaigns list uses for the same call. */}
      <ConfirmDialog
        open={confirmingDiscard}
        destructive
        title={t`discard this plan?`}
        body={t`this deletes the plan and the brief you wrote for it. i can't get either back — you'd start a new campaign from scratch.`}
        confirmLabel={t`discard it`}
        busyLabel={t`discarding…`}
        busy={discarding}
        onConfirm={handleDiscard}
        onCancel={() => setConfirmingDiscard(false)}
      />

      {/* [s375] The refinement drawer. Mounted at the page root rather
          than inside a panel so its scrim covers the whole review. */}
      {refineOpener ? (
        <CampaignRefineDrawer
          campaign={campaign}
          opener={refineOpener}
          onClose={() => setRefineOpener(null)}
          onRefined={() => {
            // The refine rewrote campaigns.draft, and every panel on
            // this page reads from the route loader. Invalidate so the
            // founder sees the new plan behind the drawer rather than
            // the one they just asked mag to change (a CommitMind rule's
            // mutation-refresh rule).
            void router.invalidate();
          }}
        />
      ) : null}
    </main>
  );
}

/** Pixel gate — fires when the founder hits launch but no analytics
 *  tracker has received its first event yet. Two options: route to
 *  the install page or ack the "flying blind" risk and proceed. */
function PixelGateModal({
  brandSlug: _brandSlug,
  onInstallClick,
  onLaunchAnyway,
  onDismiss,
}: {
  brandSlug: string;
  onInstallClick: () => void;
  onLaunchAnyway: () => void;
  onDismiss: () => void;
}) {
  const { t } = useLingui();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — a button so the a11y rule + keyboard close work
          for free (Enter/Space dismiss). The dialog sits above it via
          a sibling, not a child, so backdrop clicks don't bubble. */}
      <button
        type="button"
        aria-label={t`dismiss`}
        onClick={onDismiss}
        className="absolute inset-0 cursor-default bg-[var(--ink)]/30"
      />
      <div
        className="relative w-full max-w-[460px] rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 shadow-[6px_6px_0_var(--plum)] sm:p-7"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pixel-gate-title"
      >
        <div className="flex items-start gap-3">
          <Mag size={56} mood="confused" />
          <div className="min-w-0">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>hold on a sec</Trans>
            </p>
            <h2
              id="pixel-gate-title"
              className="mt-1 font-display text-[20px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]"
            >
              <Trans>i'll be flying blind without your pixel.</Trans>
            </h2>
          </div>
        </div>
        <p className="mt-4 text-[13.5px] leading-relaxed text-[var(--ink)]/75">
          <Trans>
            “i can launch this. but i can't tell you if it worked — paid ads, organic posts, none of
            it — until your site sends me events. one snippet, then i can read what's actually
            happening.”
          </Trans>
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <button type="button" onClick={onLaunchAnyway} className="btn btn-ghost btn-sm">
            <Trans>launch anyway</Trans>
          </button>
          <button type="button" onClick={onInstallClick} className="btn btn-sm">
            <Trans>install pixel</Trans> {'→'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlanReviewBody({
  plan,
  campaignId,
  tracker,
  working,
  onChoice,
  elapsedSeconds,
  onRefine,
}: {
  plan: CampaignPlan;
  /** [spec preflight-the-plan] The pre-flight is fetched here rather than
   *  arriving on the loader payload: it can make an upstream Keyword Planner
   *  call on a cache miss, and the plan has to render immediately whether or
   *  not google answers. A pre-flight that delays this page gets deleted. */
  campaignId: string;
  /** Only used when the plan has analytics_event deliverables —
   *  feeds the "copy install prompt" block real tracker config. */
  tracker: AnalyticsTrackerContext | null;
  working: string | null;
  onChoice: (choice: string) => void | Promise<void>;
  elapsedSeconds: number;
  /** [s375] Opens the refinement drawer, scoped to whichever panel the
   *  founder clicked. */
  onRefine: (opener: RefineOpener) => void;
}) {
  const analyticsEvents = plan.deliverables.filter(
    (d): d is AnalyticsEventDeliverable => d.kind === 'analytics_event',
  );
  const hasAnalyticsEvents = analyticsEvents.length > 0;
  const paidHooks = plan.deliverables.filter(
    (d): d is PaidAdHookDeliverable => d.kind === 'paid_ad_hook',
  );
  const hasPaidHooks = paidHooks.length > 0;
  const instagramPosts = plan.deliverables.filter(
    (d): d is InstagramPostDeliverable => d.kind === 'instagram_post',
  );
  const metaPaidAds = plan.deliverables.filter(
    (d): d is MetaPaidAdDeliverable => d.kind === 'meta_paid_ad',
  );
  const googleSearchAds = plan.deliverables.filter(
    (d): d is GoogleSearchAdDeliverable => d.kind === 'google_search_ad',
  );

  // WHAT THE FOUNDER IS ABOUT TO BUY, priced before they buy it. Only google
  // deliverables carry keywords, so there is nothing to ask about otherwise.
  // Failure is silent on purpose: a research outage must not block a launch the
  // founder is entitled to make — the panel simply does not appear.
  const preflightQ = useQuery({
    queryKey: ['campaign-preflight', campaignId],
    queryFn: () => getCampaignPreflight(campaignId),
    enabled: googleSearchAds.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const preflights = preflightQ.data ?? [];

  return (
    <>
      <PitchBlock plan={plan} elapsedSeconds={elapsedSeconds} />

      {/* ABOVE THE ADS, DELIBERATELY. The verdict is about whether this plan can
          work at all, and a founder who reads the creative first has already
          started deciding. */}
      {preflights.map((pf) => (
        <div className="mt-6" key={pf.deliverableId}>
          <CampaignPreflightPanel preflight={pf} />
        </div>
      ))}

      {/* Analytics-events block: lifted from the old special-case
          AnalyticsSetupReviewBody. Renders only when the plan has at
          least one analytics_event deliverable. */}
      {hasAnalyticsEvents ? (
        <FullSetupPromptBlock campaignName={plan.name} events={analyticsEvents} tracker={tracker} />
      ) : null}
      {hasAnalyticsEvents && plan.prerequisites.length > 0 ? (
        <PrerequisitesBlock prerequisites={plan.prerequisites} />
      ) : null}

      {/* Audience + budget only render when mag included them (paid
          campaigns). */}
      {plan.audience || plan.budget ? (
        <div className="mt-6 grid grid-cols-1 gap-3.5 md:grid-cols-2">
          {plan.audience ? <AudiencePanel audience={plan.audience} onRefine={onRefine} /> : null}
          {plan.budget ? <BudgetPanel budget={plan.budget} onRefine={onRefine} /> : null}
        </div>
      ) : null}

      {/* Paid hooks grid + analytics events vertical list + IG posts.
          Each kind owns its own layout inside DeliverablesPanel.
          Publish actions are disabled here (the campaign isn't
          launched yet); they enable on the campaign-detail page
          post-launch. */}
      <DeliverablesPanel
        paidHooks={paidHooks}
        analyticsEvents={analyticsEvents}
        instagramPosts={instagramPosts}
        metaPaidAds={metaPaidAds}
        googleSearchAds={googleSearchAds}
        campaignName={plan.name}
        tracker={tracker}
        onRefine={onRefine}
      />

      <div className="mt-6 grid grid-cols-1 gap-3.5 md:grid-cols-2">
        <OptimizingPanel optimizingFor={plan.optimizingFor} />
        <TimelinePanel timeline={plan.timeline} />
      </div>

      {/* "↘ once this is live" — campaign-level promise; lifted from
          AnalyticsSetupReviewBody so any plan can carry it. */}
      {plan.postInstallCheck ? <PostInstallCheckBlock note={plan.postInstallCheck} /> : null}

      <HonestConcernsBlock concerns={plan.honestConcerns} />

      {plan.oneAsk ? (
        <OneAskBlock
          oneAsk={plan.oneAsk}
          working={working}
          onChoice={onChoice}
          campaignName={plan.name}
        />
      ) : (
        <PlainLaunchBlock
          working={working}
          onChoice={onChoice}
          campaignName={plan.name}
          analyticsEventCount={hasAnalyticsEvents ? analyticsEvents.length : 0}
          hasPaidHooks={hasPaidHooks}
        />
      )}
    </>
  );
}

/** Per-event tile (s182). Replaces the old EventCard component — same
 *  visual + same copy buttons + same coding-agent prompt builder, now
 *  driven by an AnalyticsEventDeliverable instead of a flat event row
 *  from AnalyticsSetupDraft. */
function AnalyticsEventTile({
  deliverable,
  index,
  total,
  campaignName,
  tracker,
}: {
  deliverable: AnalyticsEventDeliverable;
  index: number;
  total: number;
  campaignName: string;
  tracker: AnalyticsTrackerContext | null;
}) {
  const { t } = useLingui();
  // Named before the message sees them: an expression inside <Trans>
  // extracts as {0}, and a translator reordering an unnamed placeholder
  // is guessing at what it holds.
  const when = deliverable.when;
  const deliverableName = deliverable.name;
  const [copiedKey, setCopiedKey] = useState<'snippet' | 'prompt' | null>(null);
  const agentPrompt = buildEventAgentPrompt(deliverable, index, total, campaignName, tracker);
  const copyText = async (text: string, key: 'snippet' | 'prompt') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      magToast.error(t`clipboard refused.`, t`select the text and copy by hand.`);
    }
  };
  return (
    <article className="rounded-xl border border-[var(--line)] bg-[var(--paper)]/40 p-3 sm:p-4">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          <Trans>event</Trans> {'·'} {String(index).padStart(2, '0')}
        </span>
        <code className="font-mono text-[14px] font-bold tracking-tight text-[var(--ink)]">
          {deliverable.name}
        </code>
        <span className="ml-auto font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/45">
          {deliverable.snippetKind}
        </span>
      </div>
      <p className="mt-3 font-serif text-[15px] leading-[1.4] text-[var(--ink)]/85">
        “{deliverable.why}”
      </p>
      <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {'↘'} <Trans>when · {when}</Trans>
      </p>
      <div className="mt-3 rounded-lg border border-[var(--line)] bg-white p-3">
        <pre className="overflow-x-auto whitespace-pre font-mono text-[12px] leading-[1.5] text-[var(--ink)]">
          {deliverable.snippet}
        </pre>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => copyText(agentPrompt, 'prompt')}
          className="btn btn-ghost text-[11px]"
          aria-label={t`copy ai coding-agent prompt for ${deliverableName}`}
          title={t`paste into cursor / claude code / cline — instructs your ai pair-programmer to install this event idiomatically`}
        >
          {copiedKey === 'prompt' ? t`copied ✓` : t`copy ai prompt`}
          {copiedKey === 'prompt' ? null : <span className="btn-arrow">→</span>}
        </button>
        <button
          type="button"
          onClick={() => copyText(deliverable.snippet, 'snippet')}
          className="btn btn-ghost text-[11px]"
          aria-label={`copy ${deliverable.name} snippet`}
        >
          {copiedKey === 'snippet' ? <Trans>copied ✓</Trans> : <Trans>copy snippet</Trans>}
          {copiedKey === 'snippet' ? null : <span className="btn-arrow">→</span>}
        </button>
      </div>
    </article>
  );
}

/** Plan-level "↘ before any of this fires" block (s182 — lifted from
 *  the old AnalyticsSetupReviewBody). Any plan can carry
 *  prerequisites now, not just analytics setups. */
function PrerequisitesBlock({ prerequisites }: { prerequisites: string[] }) {
  return (
    <div className="mt-3 rounded-2xl border border-dashed border-[var(--ink)]/25 bg-[var(--paper)] p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>before any of this fires</Trans>
      </p>
      <ul className="mt-3 space-y-1.5">
        {prerequisites.map((p) => (
          <li key={p} className="flex gap-2 text-[13px] leading-[1.45] text-[var(--ink)]/80">
            <span className="text-[var(--ink)]/40">·</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Plan-level "↘ once this is live" block (s182). Mag's promise for
 *  what happens after launch. Optional on plans. */
function PostInstallCheckBlock({ note }: { note: string }) {
  return (
    <div className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>once this is live</Trans>
      </p>
      <p className="mt-2 font-serif text-[16px] leading-[1.45] text-[var(--ink)]">“{note}”</p>
    </div>
  );
}

/** A "shortcut" panel right under the pitch. For founders who already work
 *  with an ai coding agent (cursor / claude code / cline), one paste hands
 *  off the entire install — prerequisites + every event + the brand's real
 *  tracker config (public key, allowed hosts, ingest URL) — in mag's words.
 *  Keeps the by-hand path (snippet boxes below) intact for everyone else.
 *
 *  s182 — input switched from a whole AnalyticsSetupDraft to the
 *  analytics_event deliverables + campaign name explicitly so the
 *  block can render off any plan that carries events. */
function FullSetupPromptBlock({
  campaignName,
  events,
  tracker,
}: {
  campaignName: string;
  events: AnalyticsEventDeliverable[];
  tracker: AnalyticsTrackerContext | null;
}) {
  const { t } = useLingui();
  const eventCount = events.length;
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildFullSetupAgentPrompt(campaignName, events, tracker));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      magToast.error(t`clipboard refused.`, t`select the text and copy by hand.`);
    }
  };
  return (
    <div className="mt-6 rounded-2xl border border-dashed border-[var(--plum)]/40 bg-[var(--spell-soft)] p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>shortcut · paste into a coding agent</Trans>
      </p>
      <p className="mt-2 text-[13px] leading-[1.45] text-[var(--ink)]/80">
        <Trans>
          if you work with an ai pair-programmer (cursor, claude code, cline), hand it the whole
          install in one paste — your tracker config (public key, allowed hosts, ingest url),
          prerequisites, and all {eventCount} events, with the spots each one needs to fire. it'll
          wire them in idiomatically. otherwise, install by hand below.
        </Trans>
      </p>
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={onCopy}
          className="btn btn-ghost text-[11px]"
          aria-label={t`copy full install prompt for ai coding agent`}
        >
          {copied ? t`copied ✓` : t`copy install prompt`}
          {copied ? null : <span className="btn-arrow">→</span>}
        </button>
      </div>
    </div>
  );
}

/** Format the brand's tracker context as a context block for the
 *  coding-agent prompt. Real values when we have them; honest absence
 *  notes when we don't. Returns the empty string if there's no
 *  tracker — the founder gets a generic prompt rather than fake
 *  values. */
function trackerContextBlock(tracker: AnalyticsTrackerContext | null): string {
  if (!tracker) {
    return `tracker config
- no analytics tracker has been provisioned for this brand yet. the public sdk key, allowed hosts, and ingest url are not available — ask me for them before installing snippets that reference them.
`;
  }
  const hostsLine =
    tracker.allowedHosts.length > 0
      ? tracker.allowedHosts.join(', ')
      : '(none configured — events will be rejected by the server until at least one host is added)';
  const writeKeyLine = tracker.hasWriteKey
    ? 'i already have a server-side write key generated for this tracker. when you wire up server-side snippets, read it from `process.env.MAG_WRITE_KEY` — do NOT ask me for its value (i have it in my secret store).'
    : 'a server-side write key has NOT been generated yet for this tracker. if any snippet below is server-side, FIRST tell me to generate one at dashboard → analytics → server write key. do not invent a placeholder value.';
  return `tracker config (real values — use these, don't placeholder)
- public sdk key: \`${tracker.publicKey}\` (this is what's already in my \`<script src="mag.js" data-project="...">\` tag)
- allowed origins on this tracker: ${hostsLine}
- server-ingest url for webhook events: \`${tracker.ingestUrl}\` (POST here with header \`X-Mag-Write-Key: $MAG_WRITE_KEY\`)
- write key status: ${writeKeyLine}
`;
}

/** Tracker-aware SDK contract block — pinned at the top of every
 *  copy-paste prompt so coding agents don't fabricate methods. Matches
 *  the contract baked into the analytics_setup agent's system prompt. */
const SDK_CONTRACT_BLOCK = `mag sdk contract (this is the entire public surface — do not call methods not listed here)
- \`window.mag.track(name, properties?)\` — fire a client-side event. attaches to window once the mag.js script tag loads.
- \`window.mag.identify(userId, traits?)\` — attach a stable user id to subsequent events. persisted in localStorage by default, or in a parent-domain cookie when data-cookie-domain is set on the script tag.
- \`window.mag.getDistinctId()\` — read the visitor's current distinct id. returns null only on the very first page load before any event has fired.
- \`window.mag.flush()\` — returns a Promise; flushes the queue. mostly internal, used on page unload.

script-tag options (set as attributes on the <script src="mag.js"> tag — do not invent new ones):
- \`data-cookie-domain="<parent_domain>"\` — store anon_id + identified user in a cookie on the parent domain so subdomains share it. e.g. \`data-cookie-domain="example.com"\` (leading dot added automatically). without this, the sdk uses localStorage (per-origin).
- \`data-debug="true"\` — console logging.
- \`data-disable="true"\` — kill switch.

cross-domain stitching (only relevant if the funnel crosses subdomains/hosts):
- preferred: set data-cookie-domain on the script tag on both subdomains. anon_id is shared automatically — no extra code at links.
- fallback (no cookies): on outbound cta clicks, append \`?mag_did=\` + window.mag.getDistinctId() to the destination url. the destination sdk adopts it on init and strips the param. no identify() call needed for this.

NOT in the api (do not invent these): mag.init({ cookieDomain }) (cookieDomain comes from the script tag, not a runtime call), any client-side write key.
`;

/** Build the per-event prompt that a founder pastes into their coding
 *  agent. Includes the SDK contract, real tracker config from the
 *  brand, and the event spec. Designed for cursor / claude code /
 *  cline — directive, lowercase to match the product voice. */
function buildEventAgentPrompt(
  event: AnalyticsEventDeliverable,
  index: number,
  total: number,
  campaignName: string,
  tracker: AnalyticsTrackerContext | null,
): string {
  return `i'm adding analytics event tracking to this codebase. my growth strategist (mag) drafted the event below. install it.

context
- event ${index} of ${total} in this setup pass
- campaign: ${campaignName}
- analytics joins on the exact event name string — never rename it

${trackerContextBlock(tracker)}
${SDK_CONTRACT_BLOCK}
event spec
- name: ${event.name}
- snippet kind: ${event.snippetKind}
- when to fire: ${event.when}
- why it matters: ${event.why}

snippet
\`\`\`
${event.snippet}
\`\`\`

what to do
1. find the spot in the codebase that matches the "when to fire" line above
2. integrate the snippet idiomatically for this stack (react effect/handler, framework hook, vanilla — whatever fits the surrounding code)
3. confirm the mag.js sdk is loaded on the page where this fires (check index.html / _document.tsx / root layout)
4. keep the event name exactly: \`${event.name}\`
5. when done, tell me which files you touched and where the event will fire
`;
}

/** Master prompt — bundles tracker config + sdk contract + every
 *  event into one paste. Mirrors the per-event format so the agent
 *  doesn't have to switch context.
 *
 *  s182 — signature switched from a whole AnalyticsSetupDraft to
 *  (campaignName, events, tracker). prerequisites + postInstallCheck
 *  are plan-level fields now; they're rendered separately by
 *  PrerequisitesBlock + PostInstallCheckBlock so they're surfaced to
 *  the founder visually too. Could thread them into this prompt later
 *  if we want one paste to carry everything — for now the per-tile
 *  copy + the screen-level blocks cover the install path. */
function buildFullSetupAgentPrompt(
  campaignName: string,
  events: AnalyticsEventDeliverable[],
  tracker: AnalyticsTrackerContext | null,
): string {
  const eventsBlock = events
    .map((e, i) => {
      const num = String(i + 1).padStart(2, '0');
      return `### event ${num} — ${e.name}
- when: ${e.when}
- why: ${e.why}
- snippet kind: ${e.snippetKind}

\`\`\`
${e.snippet}
\`\`\``;
    })
    .join('\n\n');
  return `i'm wiring up analytics for "${campaignName}". my growth strategist (mag) drafted ${events.length} events. install everything below.

ground rules
- analytics joins on exact event name strings — never rename an event
- match each event to the spot in the codebase its "when" describes
- integrate idiomatically (react component / framework hook / vanilla — pick what fits)
- the mag.js sdk must be loaded on every page where these events fire
- after install, tell me which files you touched and why you picked those spots

${trackerContextBlock(tracker)}
${SDK_CONTRACT_BLOCK}
events to install (in order)

${eventsBlock}
`;
}

function secondsBetween(start: string, end: string | null): number {
  if (!end) return 0;
  return Math.max(1, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000));
}

function PitchBlock({ plan, elapsedSeconds }: { plan: CampaignPlan; elapsedSeconds: number }) {
  const { t } = useLingui();
  const goal = plan.goal;
  const planName = plan.name;
  const headline = pitchHeadline(plan, t);
  return (
    // SAME MEASURE AS PanelSynthesisCard, which sits directly above it. That
    // card was capped at 68ch to kill a dead gutter and a 110-char line; this
    // one was left full-width, so the two stacked mag blocks ended up with
    // different left edges AND different right edges and read as broken
    // alignment. Capping here is what makes them one column again.
    <div className="flex max-w-[calc(68ch+2.5rem)] items-start gap-3">
      <Mag size={44} mood="smug" />
      <div className="flex-1">
        <p className="text-[12px] font-bold leading-tight">
          mag{' '}
          <span className="font-normal text-[var(--ink)]/50">
            ·{' '}
            {elapsedSeconds > 0 ? (
              <Trans>{elapsedSeconds} seconds later</Trans>
            ) : (
              <Trans>just now</Trans>
            )}
          </span>
        </p>
        <div className="mt-2 rounded-[16px_16px_16px_4px] border-[1.5px] border-[var(--ink)] bg-[var(--ink)] p-5 text-[var(--cream)] shadow-[4px_4px_0_var(--plum)]">
          <p className="font-serif text-[24px] leading-tight text-[var(--spell)]">
            <Trans>“{headline}”</Trans>
          </p>
          <p className="mt-3 text-[14.5px] leading-[1.55] opacity-90">{plan.pitch}</p>
          {/* THE LABEL IS AN EYEBROW; THE GOAL IS A SENTENCE. They had
              the same treatment — mono, 10px, uppercase, 0.12em tracking
              — which is built for three words and unreadable across two
              wrapped lines of Spanish prose. The founder reported it as
              a contrast problem; contrast measures ~7.8:1 here, which
              passes AA, so the caps and the tracking were doing the
              damage. Raised the opacity anyway because it costs nothing,
              but the fix is the typography. */}
          {plan.goal ? (
            <div className="mt-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--spell)]/90">
                {'↘'} <Trans>what i'm anchoring on</Trans>
              </p>
              <p className="mt-1 text-[13px] leading-[1.5] text-[var(--cream)]/85">{plan.goal}</p>
            </div>
          ) : null}
          {/* This one genuinely IS a short label, so the eyebrow suits
              it. Only the opacity moves: 55% of cream on ink is dim
              enough to read as disabled. */}
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--cream)]/70">
            {'↘'} <Trans>campaign name · {plan.name}</Trans>
          </p>
        </div>
      </div>
    </div>
  );
}

/** Mag-voice headline above the pitch. The old draft hard-coded
 *  "here's what i'd run. one ask, then i'll get started." — that
 *  assumes there IS a one-ask. Plans without an oneAsk get a
 *  different lead-in that doesn't promise a fork. */
function pitchHeadline(plan: CampaignPlan, t: T): string {
  if (plan.oneAsk) return i18n._(msg`here's what i'd run. one ask, then i'll get started.`);
  return i18n._(msg`here's what i'd run.`);
}

function Panel({
  eyebrow,
  children,
  className = '',
  action,
}: {
  eyebrow: string;
  children: React.ReactNode;
  className?: string;
  /** [s375] `onClick` is what makes this a real control. Every panel on
   *  the review surface passes one — it opens the refinement drawer
   *  scoped to that panel.
   *
   *  It stays optional because DeliverablesPanel is shared with the
   *  live-campaign page (brand.$slug.campaigns.$id.tsx), where a plan
   *  is past refining. There the action renders as a plain "soon" note
   *  instead of a button that does nothing: the old version was
   *  `disabled` with a title tooltip, which on touch is a dead control
   *  with no explanation at all. */
  action?: { label: string; onClick?: () => void };
}) {
  return (
    <article
      className={`rounded-2xl border-[1.5px] border-[var(--line)] bg-white p-4 sm:p-5 ${className}`}
    >
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          ↘ {eyebrow}
        </p>
        {action?.onClick ? (
          <button type="button" onClick={action.onClick} className="link-quiet text-[11px]">
            {action.label}
          </button>
        ) : action ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/35">
            {/* This branch renders ONLY where there is no onClick, which per
                the prop comment above is the live-campaign page — a plan
                past refining. "editable while drafting" described the draft
                surface the founder has already left, so on a shipped
                campaign it read as a claim about the current state and
                invited the obvious "it is not in draft anymore". Say why
                the control is absent instead of describing a past one, and
                stay true whether the campaign is live, paused or not yet
                serving. */}
            <Trans>locked after launch</Trans>
          </span>
        ) : null}
      </div>
      <div className="mt-3">{children}</div>
    </article>
  );
}

function AudiencePanel({
  audience,
  onRefine,
}: {
  audience: NonNullable<CampaignPlan['audience']>;
  onRefine?: (opener: RefineOpener) => void;
}) {
  const { t } = useLingui();
  return (
    <Panel
      eyebrow={t`audience`}
      action={{
        label: t`↘ tweak audience`,
        ...(onRefine
          ? {
              onClick: () =>
                onRefine({
                  subject: t`the audience`,
                  opener: t`tell me who you'd rather i go after — a different segment, tighter targeting, someone i left out. i'll re-draft the plan around it.`,
                }),
            }
          : {}),
      }}
    >
      <p className="text-[15px] leading-snug text-[var(--ink)]">{audience.description}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {audience.tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center rounded-full bg-[var(--paper)] px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--ink)]/75"
          >
            {tag}
          </span>
        ))}
      </div>
      <p className="mt-4 font-display text-[28px] font-black leading-none tracking-[-0.03em]">
        {audience.reachable}
      </p>
      <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
        {'↘'} <Trans>reachable</Trans>
      </p>
    </Panel>
  );
}

/** [s375] Deliverable panels all open the same drawer; only the subject
 *  changes. Built here so each call site stays a one-liner and the copy
 *  stays consistent. */
function deliverableOpener(subject: string, t: T): RefineOpener {
  return {
    subject,
    opener: i18n._(
      msg`tell me what to change about ${subject} — a different angle, more of them, fewer of them, a pitch that isn't landing. i'll re-draft.`,
    ),
  };
}

const PLATFORM_LABEL: Record<string, string> = {
  meta: 'meta',
  google: 'google',
  tiktok: 'tiktok',
};

function BudgetPanel({
  budget,
  onRefine,
}: {
  budget: NonNullable<CampaignPlan['budget']>;
  onRefine?: (opener: RefineOpener) => void;
}) {
  const { t } = useLingui();
  const budgetPhase = budget.phase;
  return (
    <Panel
      eyebrow={t`budget`}
      action={{
        label: t`↘ adjust split`,
        ...(onRefine
          ? {
              onClick: () =>
                onRefine({
                  subject: t`the budget`,
                  opener: t`say what you want the money doing — a different daily number, or a different split across platforms. i'll tell you what it costs us in signal.`,
                }),
            }
          : {}),
      }}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-display text-[32px] font-black leading-none tracking-[-0.03em]">
          ${budget.dailyUsd}
        </span>
        <span className="font-mono text-[11px] text-[var(--ink)]/60">
          <Trans>/ day · {budgetPhase}</Trans>
        </span>
      </div>
      <p className="mt-2 text-[13.5px] leading-[1.5] text-[var(--ink)]/75">{budget.rationale}</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {budget.split.map((s) => {
          const splitDaily = s.dailyUsd;
          return (
            <div
              key={s.platform}
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] p-3"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                {PLATFORM_LABEL[s.platform] ?? s.platform}
              </p>
              <p className="mt-1 font-display text-[18px] font-bold leading-none tracking-[-0.02em]">
                <Trans>${splitDaily}/d</Trans>
              </p>
              <p className="mt-1 text-[11.5px] leading-[1.4] text-[var(--ink)]/65">{s.rationale}</p>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

const PAID_AD_BG_CLASS: Record<PaidAdHookDeliverable['bg'], string> = {
  spell: 'bg-[var(--spell)] text-[var(--ink)]',
  spell_soft: 'bg-[var(--spell-soft)] text-[var(--ink)]',
  cream: 'bg-[var(--cream)] text-[var(--ink)]',
  ink: 'bg-[var(--ink)] text-[var(--spell)]',
};

/** Generic deliverables panel (s181, expanded s182). Mag picks the
 *  count + the mix; this component renders the kinds she shipped,
 *  each group in its own layout. paid_ad_hook tiles render in a
 *  2/3/6-col grid (square tiles); analytics_event deliverables
 *  render as vertical per-event cards with snippet + copy buttons.
 *  Future kinds (instagram_post, landing_change, etc.) each get
 *  their own tile component + layout case here. */
export function DeliverablesPanel({
  /** Spec da3515af — per-deliverable platform serving readings from the
   *  campaign detail endpoint. Absent on the pre-launch review screen,
   *  where nothing has been pushed yet and there is nothing to read. */
  servingStatus,
  /** Spec da3515af follow-up — wired only by the live-campaign page. */
  onCheckGoogleAdStatus,
  checkingStatusDeliverableId,
  paidHooks,
  analyticsEvents,
  instagramPosts,
  metaPaidAds,
  campaignName,
  tracker,
  /** [s375] Opens the refinement drawer scoped to a deliverable
   *  group. Optional: the live-campaign page reuses this component for
   *  a plan that is past refining, and passes nothing. */
  onRefine,
  /** When set, IG-post tiles render with a publish button that
   *  calls this callback. Without it (review screen, pre-launch),
   *  tiles render preview-only with a "publish after you ship the
   *  campaign" hint. */
  onPublishInstagramPost,
  /** Local in-flight publish marker — the parent sets this to the
   *  deliverableId currently being published so the tile can show a
   *  spinner. */
  publishingDeliverableId,
  /** When set, IG-post tiles render a "generate image" button that
   *  calls this callback. s190 wiring. */
  onGenerateInstagramImage,
  /** Local in-flight image-gen marker — same pattern as publishing. */
  generatingImageDeliverableId,
  /** s193 — when set, meta_paid_ad tiles render with a launch
   *  button. Same pattern as onPublishInstagramPost but the
   *  underlying API path is the same (publishDeliverable). */
  onLaunchMetaPaidAd,
  launchingDeliverableId,
  /** s193 — meta_paid_ad's image is the ad creative; gen path is
   *  shared with IG posts. */
  onGenerateMetaPaidAdImage,
  generatingMetaPaidAdImageId,
  /** s194 — meta_paid_ad metrics refresh handler. When set, launched
   *  tiles render a "refresh metrics" button + cached metrics block. */
  onRefreshMetaPaidAdMetrics,
  refreshingMetricsDeliverableId,
  trendCampaignId,
  googleSearchAds,
  googleCustomerId,
  /** s205 — when set, google_search_ad tiles render with a launch
   *  button that hits the publishDeliverable endpoint. Without it
   *  (review screen pre-launch), tiles render preview-only with a
   *  "launch unlocks after campaign ships" hint. */
  onLaunchGoogleSearchAd,
  launchingGoogleSearchAdId,
  onApproveAutodraft,
  approvingAutodraftId,
  onUnpauseDeliverable,
  unpausingDeliverableId,
  onPauseDeliverable,
  pausingDeliverableId,
  ownsSpacing = true,
}: {
  servingStatus?: Record<string, DeliverableServingStatus> | undefined;
  onCheckGoogleAdStatus?: ((deliverableId: string) => void | Promise<void>) | undefined;
  checkingStatusDeliverableId?: string | null | undefined;
  paidHooks: PaidAdHookDeliverable[];
  analyticsEvents: AnalyticsEventDeliverable[];
  instagramPosts: InstagramPostDeliverable[];
  metaPaidAds: MetaPaidAdDeliverable[];
  googleSearchAds: GoogleSearchAdDeliverable[];
  /** Threaded from the campaign page, which already queries the brand's
   *  connected ad accounts — no new fetch for the deep link. */
  googleCustomerId?: string | null;
  campaignName: string;
  tracker: AnalyticsTrackerContext | null;
  onPublishInstagramPost?: (deliverableId: string) => void | Promise<void>;
  publishingDeliverableId?: string | null;
  onGenerateInstagramImage?: (deliverableId: string) => void | Promise<void>;
  generatingImageDeliverableId?: string | null;
  onLaunchMetaPaidAd?: (deliverableId: string) => void | Promise<void>;
  launchingDeliverableId?: string | null;
  onGenerateMetaPaidAdImage?: (deliverableId: string) => void | Promise<void>;
  generatingMetaPaidAdImageId?: string | null;
  onRefreshMetaPaidAdMetrics?: (deliverableId: string) => void | Promise<void>;
  refreshingMetricsDeliverableId?: string | null;
  /** autopilot A3b-finish — when set, paid-ad tiles render a
   *  "resume" button on auto-paused deliverables that calls this
   *  callback. */
  onUnpauseDeliverable?: (deliverableId: string) => void | Promise<void>;
  unpausingDeliverableId?: string | null;
  /** When set, a LAUNCHED and not-already-paused paid tile renders a
   *  "pause this ad" button. Only the live campaign page passes it —
   *  the review screen has nothing launched to stop. */
  onPauseDeliverable?: (deliverableId: string) => void | Promise<void>;
  pausingDeliverableId?: string | null;
  /** Does this panel carry its own top margin? False when a container
   *  already owns the vertical rhythm — the campaign page stacks these in a
   *  gap-6 column, where the panel's own mt-6 doubles the gap. */
  ownsSpacing?: boolean;
  /** s197 — when set, the meta_paid_ad tiles render a trend chart
   *  below their latest metrics. The detail page passes its
   *  campaignId; the review screen doesn't (nothing's launched
   *  there). */
  trendCampaignId?: string;
  onLaunchGoogleSearchAd?: (deliverableId: string) => void | Promise<void>;
  launchingGoogleSearchAdId?: string | null;
  /** A6-finish — approve an autopilot-drafted google_search_ad,
   *  clearing autopilot_drafted_at so A4 picks it up. */
  onApproveAutodraft?: (deliverableId: string) => void | Promise<void>;
  approvingAutodraftId?: string | null;
  /** [s375] See the destructured doc above. */
  onRefine?: (opener: RefineOpener) => void;
}) {
  const { t } = useLingui();
  const total =
    paidHooks.length +
    analyticsEvents.length +
    instagramPosts.length +
    metaPaidAds.length +
    googleSearchAds.length;
  if (total === 0) {
    return (
      <Panel
        eyebrow={t`deliverables`}
        className={ownsSpacing ? 'mt-6' : ''}
        action={{
          label: t`↘ ask mag for more`,
          ...(onRefine
            ? { onClick: () => onRefine(deliverableOpener(t`the deliverables`, t)) }
            : {}),
        }}
      >
        <p className="font-serif text-[14px] leading-[1.5] text-[var(--ink)]/55">
          <Trans>mag didn't ship any deliverables on this draft.</Trans>
        </p>
      </Panel>
    );
  }
  // WHO OWNS THE VERTICAL RHYTHM. The review screen stacks these panels
  // directly, so the mt-6 is theirs to carry. The campaign page puts them in
  // a flex column with gap-6, where the two stack to 48px — and the gap below
  // the evidence disclosure read as twice the gap above it. Same bug the
  // ask-mag panels had; the container should own spacing wherever there IS a
  // container.
  return (
    <div className={`${ownsSpacing ? 'mt-6 ' : ''}flex flex-col gap-3.5`}>
      {paidHooks.length > 0 ? (
        <Panel
          eyebrow={plural(paidHooks.length, {
            one: 'creative · # hook',
            other: 'creative · # hooks',
          })}
          action={{
            label: t`↘ ask mag for more`,
            ...(onRefine ? { onClick: () => onRefine(deliverableOpener(t`the creative`, t)) } : {}),
          }}
        >
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-6">
            {paidHooks.map((d) => (
              <PaidAdHookTile key={d.id} deliverable={d} />
            ))}
          </div>
        </Panel>
      ) : null}
      {instagramPosts.length > 0 ? (
        <Panel
          eyebrow={plural(instagramPosts.length, {
            one: 'instagram post · #',
            other: 'instagram posts · #',
          })}
          action={{
            label: t`↘ ask mag for more`,
            ...(onRefine
              ? { onClick: () => onRefine(deliverableOpener(t`the instagram posts`, t)) }
              : {}),
          }}
        >
          <div className="flex flex-col gap-3">
            {instagramPosts.map((d) => (
              <InstagramPostTile
                key={d.id}
                deliverable={d}
                {...(onPublishInstagramPost
                  ? { onPublish: () => onPublishInstagramPost(d.id) }
                  : {})}
                isPublishing={publishingDeliverableId === d.id}
                {...(onGenerateInstagramImage
                  ? { onGenerateImage: () => onGenerateInstagramImage(d.id) }
                  : {})}
                isGeneratingImage={generatingImageDeliverableId === d.id}
              />
            ))}
          </div>
        </Panel>
      ) : null}
      {metaPaidAds.length > 0 ? (
        <Panel
          eyebrow={plural(metaPaidAds.length, {
            one: 'meta paid ad · #',
            other: 'meta paid ads · #',
          })}
          action={{
            label: t`↘ ask mag for more`,
            ...(onRefine ? { onClick: () => onRefine(deliverableOpener(t`the meta ads`, t)) } : {}),
          }}
        >
          <div className="flex flex-col gap-3">
            {metaPaidAds.map((d) => (
              <MetaPaidAdTile
                key={d.id}
                deliverable={d}
                {...(onLaunchMetaPaidAd ? { onLaunch: () => onLaunchMetaPaidAd(d.id) } : {})}
                isLaunching={launchingDeliverableId === d.id}
                {...(onGenerateMetaPaidAdImage
                  ? { onGenerateImage: () => onGenerateMetaPaidAdImage(d.id) }
                  : {})}
                isGeneratingImage={generatingMetaPaidAdImageId === d.id}
                {...(onRefreshMetaPaidAdMetrics
                  ? { onRefreshMetrics: () => onRefreshMetaPaidAdMetrics(d.id) }
                  : {})}
                isRefreshingMetrics={refreshingMetricsDeliverableId === d.id}
                {...(trendCampaignId ? { trendCampaignId } : {})}
                {...(onUnpauseDeliverable ? { onUnpause: () => onUnpauseDeliverable(d.id) } : {})}
                {...(onPauseDeliverable ? { onPause: () => onPauseDeliverable(d.id) } : {})}
                isPausing={pausingDeliverableId === d.id}
                isUnpausing={unpausingDeliverableId === d.id}
              />
            ))}
          </div>
        </Panel>
      ) : null}
      {googleSearchAds.length > 0 ? (
        <Panel
          eyebrow={plural(googleSearchAds.length, {
            one: 'google search ad · #',
            other: 'google search ads · #',
          })}
          action={{
            label: t`↘ ask mag for more`,
            ...(onRefine
              ? { onClick: () => onRefine(deliverableOpener(t`the google search ads`, t)) }
              : {}),
          }}
        >
          <div className="flex flex-col gap-3">
            {googleSearchAds.map((d) => (
              <GoogleSearchAdTile
                key={d.id}
                deliverable={d}
                googleCustomerId={googleCustomerId ?? null}
                {...(onRefine
                  ? {
                      // Seeded with THIS ad's id, so mag opens knowing
                      // which one the founder is looking at.
                      onRefineThisAd: () =>
                        onRefine(deliverableOpener(t`the google search ad "${d.id}"`, t)),
                    }
                  : {})}
                {...(onLaunchGoogleSearchAd
                  ? { onLaunch: () => onLaunchGoogleSearchAd(d.id) }
                  : {})}
                isLaunching={launchingGoogleSearchAdId === d.id}
                {...(onCheckGoogleAdStatus
                  ? { onCheckStatus: () => onCheckGoogleAdStatus(d.id) }
                  : {})}
                isCheckingStatus={checkingStatusDeliverableId === d.id}
                {...(servingStatus
                  ? {
                      servingState: deliverableServingState(d, servingStatus),
                      // The REASON, not just the state. Google sends it with
                      // the status and we already store it; sending the
                      // founder to the ads manager to read a string we are
                      // holding is the abstraction leaking.
                      ...(servingReasonPhrase(servingStatus[d.id]?.reasons ?? [])
                        ? {
                            servingReason: servingReasonPhrase(
                              servingStatus[d.id]?.reasons ?? [],
                            ) as string,
                          }
                        : {}),
                    }
                  : {})}
                {...(onApproveAutodraft
                  ? { onApproveAutodraft: () => onApproveAutodraft(d.id) }
                  : {})}
                isApprovingAutodraft={approvingAutodraftId === d.id}
                {...(onUnpauseDeliverable ? { onUnpause: () => onUnpauseDeliverable(d.id) } : {})}
                {...(onPauseDeliverable ? { onPause: () => onPauseDeliverable(d.id) } : {})}
                isPausing={pausingDeliverableId === d.id}
                isUnpausing={unpausingDeliverableId === d.id}
              />
            ))}
          </div>
        </Panel>
      ) : null}
      {analyticsEvents.length > 0 ? (
        <Panel
          eyebrow={plural(analyticsEvents.length, {
            one: 'analytics event · #',
            other: 'analytics events · #',
          })}
        >
          <div className="flex flex-col gap-3">
            {analyticsEvents.map((d, i) => (
              <AnalyticsEventTile
                key={d.id}
                deliverable={d}
                index={i + 1}
                total={analyticsEvents.length}
                campaignName={campaignName}
                tracker={tracker}
              />
            ))}
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

/** Small chevron toggle pinned to the top-right of an ad tile. Used
 *  by GoogleSearchAdTile + MetaPaidAdTile so launched ads can collapse
 *  to a 1-line header and the founder can scan a campaign of N ads
 *  without scrolling forever. Rotates 180° on expand. */
function PaidAdHookTile({ deliverable }: { deliverable: PaidAdHookDeliverable }) {
  return (
    <div
      className={`flex aspect-square items-center justify-center rounded-[10px] border-[1.5px] border-[var(--ink)]/40 p-2 ${PAID_AD_BG_CLASS[deliverable.bg]}`}
      title={deliverable.why}
    >
      <p className="whitespace-pre-line text-center font-serif text-[12.5px] leading-[1.05]">
        {deliverable.hook}
      </p>
    </div>
  );
}

/** One Instagram-post deliverable tile (s183). Renders mag's caption
 *  + image brief + post-when as a preview card, with a publish button
 *  on launched campaigns. Once published, the button is replaced by
 *  the shipped state (mock badge when published_via='mock'). */
function InstagramPostTile({
  deliverable,
  onPublish,
  isPublishing,
  onGenerateImage,
  isGeneratingImage,
}: {
  deliverable: InstagramPostDeliverable;
  /** When provided, the publish button is rendered + active. Absent
   *  → the tile is preview-only with a hint that publish unlocks
   *  post-launch (review screen case). */
  onPublish?: () => void | Promise<void>;
  isPublishing?: boolean;
  /** When provided, the "generate image" button is rendered. Without
   *  it (review screen), the tile shows image brief but no gen
   *  affordance. */
  onGenerateImage?: () => void | Promise<void>;
  isGeneratingImage?: boolean;
}) {
  const { t } = useLingui();
  const postWhen = deliverable.post_when;
  const published = Boolean(deliverable.published_at);
  const isMockPublish = deliverable.published_via === 'mock';
  const hasImage = Boolean(deliverable.image_url);
  return (
    <article className="rounded-xl border-[1.5px] border-[var(--ink)] bg-white p-3 shadow-[3px_3px_0_var(--spell-soft)] sm:p-4">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>instagram post</Trans>
        </span>
        <code className="font-mono text-[10px] tracking-tight text-[var(--ink)]/60">
          {deliverable.id}
        </code>
        {published ? (
          <span
            className={`ml-auto inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] ${
              isMockPublish
                ? 'border border-[var(--ink)]/30 bg-[var(--paper)] text-[var(--ink)]/65'
                : 'border border-[var(--ink)] bg-[var(--spell-soft)] text-[var(--ink)]'
            }`}
            title={
              isMockPublish
                ? t`mock publish — nothing was actually posted to instagram. real graph api wiring lands in s184.`
                : t`published to instagram`
            }
          >
            {isMockPublish ? t`mock-shipped` : t`shipped`}
          </span>
        ) : null}
      </div>
      {hasImage && deliverable.image_url ? (
        <div className="mt-3 overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--paper)]">
          <img
            src={deliverable.image_url}
            alt={`preview for ${deliverable.id}`}
            className="aspect-square w-full object-cover"
            loading="lazy"
          />
        </div>
      ) : null}
      <p className="mt-3 whitespace-pre-wrap font-serif text-[15px] leading-[1.5] text-[var(--ink)]">
        {deliverable.caption}
      </p>
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          {'↘'} <Trans>image brief</Trans>
        </p>
        {onGenerateImage && !published ? (
          <button
            type="button"
            onClick={() => void onGenerateImage()}
            disabled={isGeneratingImage}
            className="btn btn-ghost text-[11px] disabled:opacity-60"
            title={
              hasImage
                ? t`regenerate the image — overwrites the current one`
                : t`render the image with mag's image gen`
            }
          >
            {isGeneratingImage ? t`rendering…` : hasImage ? t`regenerate image` : t`generate image`}
            <span className="btn-arrow">→</span>
          </button>
        ) : null}
      </div>
      <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/75">
        {deliverable.image_brief}
      </p>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {'↘'} <Trans>post when · {postWhen}</Trans>
      </p>
      <p className="mt-2 mag-voice text-[13px] leading-[1.5] text-[var(--ink)]/65">
        “{deliverable.why}”
      </p>
      {published ? (
        <div className="mt-3 flex items-center justify-end gap-2 border-t border-[var(--line)] pt-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            ↘ {deliverable.published_post_id}
          </span>
          <span className="font-mono text-[10px] text-[var(--ink)]/45">
            ·{' '}
            {deliverable.published_at
              ? formatDateTime(deliverable.published_at, {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : ''}
          </span>
        </div>
      ) : onPublish ? (
        <div className="mt-3 flex items-center justify-end gap-3 border-t border-[var(--line)] pt-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            {'↘'} <Trans>mock publish for now — real instagram wiring lands soon</Trans>
          </span>
          <button
            type="button"
            onClick={() => void onPublish()}
            disabled={isPublishing}
            className="btn disabled:opacity-60"
          >
            {isPublishing ? t`shipping…` : t`publish`}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      ) : (
        <div className="mt-3 border-t border-[var(--line)] pt-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            {'↘'} <Trans>publish unlocks once you ship the campaign</Trans>
          </p>
        </div>
      )}
    </article>
  );
}

/** One google_search_ad deliverable tile (s205). Shows mag's
 *  campaign proposal: name, ad-group, budget, headlines + descriptions
 *  preview, keywords with match-type chips, audience prose, the why
 *  line. Pre-launch (review screen) renders preview-only with a
 *  "launch unlocks once you ship the campaign" hint; on the campaign
 *  detail page after launch, an `onLaunch` callback wires the
 *  "launch on google" button which kicks the 5-step create chain.
 *
 *  Launched state shows the campaign id + a launched/mock badge —
 *  same pattern as the meta tile so founders read the two surfaces
 *  uniformly. */
function GoogleSearchAdTile({
  deliverable,
  googleCustomerId,
  onLaunch,
  isLaunching,
  onApproveAutodraft,
  isApprovingAutodraft,
  onUnpause,
  isUnpausing,
  onPause,
  isPausing,
  servingState,
  servingReason,
  onCheckStatus,
  isCheckingStatus,
  onRefineThisAd,
}: {
  deliverable: GoogleSearchAdDeliverable;
  /** The google ads customer id this brand launched into, when one is
   *  connected. Absent on the review screen (nothing is launched yet) and
   *  for a brand with no google account — the footer then prints the id
   *  without a link rather than guessing an account. */
  googleCustomerId?: string | null;
  /** Open the refine drawer seeded with THIS ad rather than the group.
   *  The panel header already offers "ask mag for more" for all the
   *  google ads at once; with two ads on screen that cannot say "change
   *  this one", which is what the founder was looking at when they asked
   *  for it. Absent on surfaces with no refine path (the live campaign
   *  page), where the tile stays preview-only. */
  onRefineThisAd?: (() => void) | undefined;
  /** Ask google right now whether this ad is serving. Present only on
   *  the live-campaign page for a real (non-mock) launch — there is
   *  nothing to ask about before that. Exists because the automatic
   *  reading lands on a 1-4h tick, and a founder watching a campaign
   *  clear review should not have to wait for a schedule they cannot
   *  see. */
  onCheckStatus?: () => void | Promise<void>;
  isCheckingStatus?: boolean;
  /** Spec da3515af — the platform's own answer about whether this ad is
   *  serving. Undefined when we have no fresh reading, which must render
   *  exactly as it did before this existed. Computed by the parent from
   *  the same shared derivation the header pill uses, so the two cannot
   *  disagree. */
  servingState?: ServingState;
  /** Mag's phrasing of WHY google restricted it, from the reasons it sent
   *  with the status. Absent when the code has no phrase yet. */
  servingReason?: string;
  onLaunch?: () => void | Promise<void>;
  isLaunching?: boolean;
  /** A6-finish — present when an autodrafted deliverable is
   *  awaiting founder approval. Clicking calls the
   *  approve-autodraft endpoint which clears autopilot_drafted_at
   *  so A4 autolaunch picks it up. */
  onApproveAutodraft?: () => void | Promise<void>;
  isApprovingAutodraft?: boolean;
  onUnpause?: () => void | Promise<void>;
  isUnpausing?: boolean;
  /** Present only where a pause can actually be dispatched — the live
   *  campaign page. Absent on the review screen, where nothing has
   *  launched yet and there is nothing to stop. */
  onPause?: () => void | Promise<void>;
  isPausing?: boolean;
}) {
  const { t } = useLingui();
  const launched = Boolean(deliverable.launched_at);
  const isMockLaunch = deliverable.launched_via === 'mock';
  const googleDailyBudget = deliverable.daily_budget_usd;
  const adGroupName = deliverable.ad_group_name;
  // [spec c441e4c2] ONE CAMPAIGN, SEVERAL AD GROUPS, ONE BUDGET. The plan can
  // carry extra ad groups sharing this deliverable's single daily budget, and
  // each group can carry a second RSA. This tile rendered only the primary
  // group and its first ad, so a founder approved what looked like one thin
  // ad group while three groups and six ads actually shipped.
  //
  // That is an approval surface showing less than it launches, which is the
  // one direction a review screen must never be wrong in.
  const extraAdGroups = deliverable.extra_ad_groups ?? [];
  const adsIn = (group: { extra_creatives?: unknown[] | undefined }) =>
    1 + (group.extra_creatives?.length ?? 0);
  const allGroups = [
    { name: adGroupName, ads: adsIn(deliverable) },
    ...extraAdGroups.map((g) => ({ name: g.ad_group_name, ads: adsIn(g) })),
  ];
  const totalAds = allGroups.reduce((sum, g) => sum + g.ads, 0);
  // [spec preflight-the-plan S4] Exclusions are CAMPAIGN-level — one list for
  // every group, which is why they are read off the deliverable rather than
  // per group below.
  const negativeKeywords = deliverable.negative_keywords ?? [];
  // Whether any keyword ANYWHERE in this campaign matches more than itself.
  // An EXACT-only campaign with no negatives is a deliberate narrow bet and
  // needs no warning; a PHRASE or BROAD keyword with nothing excluded is the
  // shape that spent fiscalo's budget on the tax authority's own portal, and
  // it has to be said on the screen where the founder approves it. The extra
  // groups carry their own keywords, so the question is asked of all of them.
  const hasLooseMatchKeyword = [
    ...deliverable.keywords,
    ...extraAdGroups.flatMap((g) => g.keywords),
  ].some((kw) => kw.matchType !== 'EXACT');
  const launchedCampaignId = deliverable.launched_campaign_id;
  const previewHeadlines = deliverable.headlines.slice(0, 3);
  const previewDescriptions = deliverable.descriptions.slice(0, 2);
  const autodrafted = Boolean(deliverable.autopilot_drafted_at) && !launched;
  // Collapse the detail body by default for ads that have shipped
  // for real — the founder has already reviewed + launched, so the
  // card collapses to a 1-line summary that's easy to scan. Mock
  // launches and not-yet-launched ads stay expanded so the founder
  // can still see what they're about to push (or what mock state
  // they're in).
  const [collapsed, setCollapsed] = useState<boolean>(launched && !isMockLaunch);
  return (
    <article className="rounded-xl border-[1.5px] border-[var(--ink)] bg-white p-3 shadow-[3px_3px_0_var(--spell-soft)] sm:p-4">
      {autodrafted ? (
        <AutopilotDraftedBadge
          {...(onApproveAutodraft ? { onApprove: onApproveAutodraft } : {})}
          isApproving={Boolean(isApprovingAutodraft)}
        />
      ) : null}
      {deliverable.auto_paused_at && deliverable.auto_pause_reason ? (
        <AutopausedBadge
          reason={deliverable.auto_pause_reason}
          {...(onUnpause ? { onUnpause } : {})}
          isUnpausing={Boolean(isUnpausing)}
        />
      ) : null}
      {!deliverable.auto_paused_at && deliverable.paused_at ? (
        <FounderPausedBadge
          reason={deliverable.pause_reason ?? ''}
          {...(onUnpause ? { onUnpause } : {})}
          isUnpausing={Boolean(isUnpausing)}
        />
      ) : null}
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>google search</Trans>
        </span>
        <code className="font-mono text-[10px] tracking-tight text-[var(--ink)]/60">
          {deliverable.id}
        </code>
        <span className="ml-auto font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>${googleDailyBudget}/day</Trans>
        </span>
        {launched ? (
          <Tooltip
            label={
              isMockLaunch
                ? t`the ad was created on our side but nothing was pushed to google ads. connect a google ads account at /channels to launch for real.`
                : servingState === 'pending'
                  ? t`we pushed this to google ads. google reviews every new ad before it serves, so it isn't showing to anyone yet — that usually clears within a day.`
                  : servingState === 'limited'
                    ? servingReason
                      ? t`google is serving this but with restrictions: ${servingReason}.`
                      : t`google is serving this but with restrictions and didn't say which. i'll keep checking.`
                    : t`launched on google ads — view it in your ads manager.`
            }
          >
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] ${
                isMockLaunch || servingState === 'pending' || servingState === 'limited'
                  ? 'border border-[var(--ink)]/30 bg-[var(--paper)] text-[var(--ink)]/65'
                  : 'border border-[var(--ink)] bg-[var(--spell-soft)] text-[var(--ink)]'
              }`}
            >
              {isMockLaunch ? (
                t`created · pending launch`
              ) : servingState === 'pending' ? (
                t`in review`
              ) : servingState === 'limited' ? (
                t`limited`
              ) : (
                <Trans>launched</Trans>
              )}
            </span>
          </Tooltip>
        ) : null}
        {onCheckStatus && launched && !isMockLaunch ? (
          <button
            type="button"
            onClick={onCheckStatus}
            disabled={isCheckingStatus}
            className="rounded-md border border-[var(--ink)]/25 bg-transparent px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isCheckingStatus ? t`checking…` : t`check now`}
          </button>
        ) : null}
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
          ariaLabel={collapsed ? t`show ad details` : t`collapse ad details`}
        />
      </div>

      <p className="mt-3 font-display text-[18px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--ink)]">
        {deliverable.campaign_name}
      </p>
      {allGroups.length === 1 ? (
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          {'↘'} <Trans>ad group · {adGroupName}</Trans>
          {allGroups[0] && allGroups[0].ads > 1 ? (
            <>
              {' · '}
              <Plural value={allGroups[0].ads} one="# ad" other="# ads" />
            </>
          ) : null}
        </p>
      ) : (
        <div className="mt-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            {'↘'}{' '}
            {/* The budget is stated HERE, next to the group count, because
                "three ad groups" reads as three budgets to anyone who has run
                google ads before — and the whole point of this shape is that
                they share one. */}
            {/* Plural, not a bare interpolation: a group can hold one ad, and
                "1 ads" is wrong in english and wronger in spanish, whose plural
                rules this catalog actually uses. */}
            <Plural value={allGroups.length} one="# ad group" other="# ad groups" />
            {' · '}
            <Plural value={totalAds} one="# ad" other="# ads" />
            {' · '}
            <Trans>sharing one ${googleDailyBudget}/day budget</Trans>
          </p>
          <ul className="mt-1.5 flex flex-col gap-0.5">
            {allGroups.map((g) => (
              <li
                key={g.name}
                className="font-mono text-[10px] tracking-[0.04em] text-[var(--ink)]/55"
              >
                {'·'} {g.name}{' '}
                <span className="text-[var(--ink)]/40">
                  (<Plural value={g.ads} one="# ad" other="# ads" />)
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <CollapsibleBlock open={!collapsed}>
        <div className="mt-4">
          <GoogleAdPreview deliverable={deliverable} />
        </div>
        {/* ONE PREVIEW PER AD GROUP. The tile said "3 ad groups · 6 ads" and
            then showed a single ad, so the founder reasonably read it as one
            ad having been created — the ads were all there (verified against
            the Ads API: 3 ad_group rows, 6 ad_group_ad rows), only one was
            ever drawn.
            Reusing GoogleAdPreview with the group's own copy rather than
            writing a second previewer: compact, because the audience and
            rationale above already carry the context for the whole
            deliverable and repeating them per group is noise. */}
        {extraAdGroups.length > 0 ? (
          <div className="mt-5 flex flex-col gap-5 border-t border-[var(--line)] pt-4">
            {extraAdGroups.map((g) => (
              <div key={g.ad_group_name}>
                <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
                  {'↘'} <Trans>ad group · {g.ad_group_name}</Trans>
                </p>
                <div className="mt-2">
                  <GoogleAdPreview
                    compact
                    deliverable={{
                      ...deliverable,
                      ad_group_name: g.ad_group_name,
                      headlines: g.headlines,
                      descriptions: g.descriptions,
                      keywords: g.keywords,
                      extra_creatives: g.extra_creatives ?? [],
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* [spec preflight-the-plan S4] WHAT THIS CAMPAIGN WILL NOT MATCH.
            Campaign-level, so it sits below the groups rather than inside any
            one of them — a negative applies to every group's keywords at once.

            THE EMPTY CASE IS RENDERED ON PURPOSE. A campaign with a PHRASE or
            BROAD keyword and no exclusions is the fiscalo shape: `e-factura
            dgi` [PHRASE] bought four variants of people hunting the tax
            authority's own portal, $19.80 of a $40.47 campaign, and nothing
            on any screen said that could happen. Silence here would read as
            "nothing to exclude" when it means "nothing excluded" — so the
            line is printed before the founder authorizes the spend, not
            after the report explains where it went.

            Every term is listed, never truncated to a "+N more": this is the
            approval surface, and showing less than what launches is the one
            direction it must not be wrong in (be752a0a). */}
        {negativeKeywords.length > 0 ? (
          <div className="mt-5 border-t border-[var(--line)] pt-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
              {'↘'} <Trans>not matching · {negativeKeywords.length}</Trans>
            </p>
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {negativeKeywords.map((kw) => (
                <li
                  key={`${kw.text}-${kw.matchType}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-[var(--ink)]/25 bg-[var(--paper)] px-2 py-0.5 font-mono text-[10.5px] text-[var(--ink)]/70"
                >
                  <span className="text-[var(--ink)]/40">{'−'}</span>
                  {kw.text}
                  <span className="text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/40">
                    {kw.matchType.toLowerCase()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : hasLooseMatchKeyword ? (
          <p className="mt-5 border-t border-[var(--line)] pt-4 font-mono text-[10px] leading-[1.6] tracking-[0.04em] text-[var(--ink)]/55">
            {'↘'}{' '}
            <Trans>
              nothing is excluded. phrase and broad keywords also match what people type around
              them, and this campaign will pay for all of it.
            </Trans>
          </p>
        ) : null}
      </CollapsibleBlock>

      {launched && !isMockLaunch ? (
        <div className="mt-3 flex items-center justify-end gap-2 border-t border-[var(--line)] pt-3">
          {googleCustomerId && launchedCampaignId ? (
            // The founder asked to be able to reach the real thing. The id
            // was already printed here and was not clickable, which is the
            // worst of both — enough information to know it exists, not
            // enough to act on it.
            //
            // __c selects the ad account (see the deep-link shape google's
            // own UI emits). Without it the link still carries campaignId,
            // so the failure mode is google's account picker rather than a
            // dead link. Digits only: the api may hold a dashed id and
            // google rejects the dashes.
            <a
              href={`https://ads.google.com/aw/campaigns?campaignId=${encodeURIComponent(launchedCampaignId)}&__c=${encodeURIComponent(googleCustomerId.replace(/\D/g, ''))}`}
              target="_blank"
              rel="noreferrer noopener"
              className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--plum)] underline decoration-[var(--plum)]/30 underline-offset-2 hover:decoration-[var(--plum)]"
              title={t`open this campaign in google ads`}
            >
              {'↗'} <Trans>open in google ads · {launchedCampaignId}</Trans>
            </a>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
              {'↘'} <Trans>campaign · {launchedCampaignId}</Trans>
            </span>
          )}
          <span className="font-mono text-[10px] text-[var(--ink)]/45">
            ·{' '}
            {deliverable.launched_at
              ? formatDateTime(deliverable.launched_at, {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : ''}
          </span>
          {/* The pause control lives HERE, with the tile's other actions, and
              not in a row of its own above the header — which is where it was
              and which rendered it floating over the card's top edge, attached
              to nothing. Rightmost in an already right-aligned footer. */}
          {onPause && !deliverable.auto_paused_at && !deliverable.paused_at ? (
            <PauseAdButton onPause={onPause} isPausing={Boolean(isPausing)} />
          ) : null}
        </div>
      ) : onLaunch ? (
        <div className="mt-3 flex items-center justify-end gap-3 border-t border-[var(--line)] pt-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            {isMockLaunch
              ? t`↘ the ad is created on our side — push to google now to go live`
              : t`↘ launches ENABLED on google — your approval is the launch gate`}
          </span>
          <button
            type="button"
            onClick={() => void onLaunch()}
            disabled={isLaunching}
            className="btn disabled:opacity-60"
          >
            {isLaunching ? t`launching…` : isMockLaunch ? t`push to google` : t`launch on google`}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[var(--line)] pt-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            {isMockLaunch
              ? t`↘ connect a google ads account at /channels to push this ad live`
              : t`↘ launch unlocks once you ship the campaign`}
          </p>
          {/* Per-ad change request. The panel header's "ask mag for more"
              is seeded with the whole group; a founder reading ONE ad
              wants to change that one. Same drawer, narrower subject —
              decision e746f3db made the chat drawer the universal
              surface for this, so this is a new entry point, not a new
              mechanism. Rendered as a real button because the header
              action reads as a label and was missed. */}
          {onRefineThisAd ? (
            <button type="button" onClick={onRefineThisAd} className="btn btn-ghost text-[11px]">
              <Trans>ask mag to change this</Trans>
              <span className="btn-arrow">→</span>
            </button>
          ) : null}
        </div>
      )}
    </article>
  );
}

/** One meta_paid_ad deliverable tile (s193). Shows mag's ad copy +
 *  audience + budget + optimization goal. Pre-launch the tile is
 *  preview-only; once the campaign is launched, the launch button
 *  ships the ad to Meta (paused) via the Marketing API. The mock /
 *  meta badge mirrors instagram_post's pattern so the founder sees
 *  at a glance which path actually fired. */
function MetaPaidAdTile({
  deliverable,
  onLaunch,
  isLaunching,
  onGenerateImage,
  isGeneratingImage,
  onRefreshMetrics,
  isRefreshingMetrics,
  trendCampaignId,
  onUnpause,
  isUnpausing,
  onPause,
  isPausing,
}: {
  deliverable: MetaPaidAdDeliverable;
  onLaunch?: () => void | Promise<void>;
  isLaunching?: boolean;
  onGenerateImage?: () => void | Promise<void>;
  isGeneratingImage?: boolean;
  onRefreshMetrics?: () => void | Promise<void>;
  isRefreshingMetrics?: boolean;
  /** s197 — when set, the tile renders a small trend chart below
   *  the latest metrics block, fetched from the metrics history
   *  endpoint. Only the campaign detail page passes this; the
   *  review screen doesn't (no launched ads yet there). */
  trendCampaignId?: string;
  onUnpause?: () => void | Promise<void>;
  isUnpausing?: boolean;
  /** Present only where a pause can actually be dispatched — the live
   *  campaign page. Absent on the review screen, where nothing has
   *  launched yet and there is nothing to stop. */
  onPause?: () => void | Promise<void>;
  isPausing?: boolean;
}) {
  const { t } = useLingui();
  const dailyBudgetUsd = deliverable.daily_budget_usd;
  const launched = Boolean(deliverable.launched_at);
  const isMockLaunch = deliverable.launched_via === 'mock';
  const hasImage = Boolean(deliverable.image_url);
  const metrics = deliverable.metrics ?? null;
  // Match GoogleSearchAdTile: real-launched ads collapse by default;
  // mock + not-yet-launched stay expanded so the founder still
  // sees creative / brief / audience while reviewing.
  const [collapsed, setCollapsed] = useState<boolean>(launched && !isMockLaunch);
  return (
    <article className="rounded-xl border-[1.5px] border-[var(--ink)] bg-white p-3 shadow-[3px_3px_0_var(--spell-soft)] sm:p-4">
      {deliverable.auto_paused_at && deliverable.auto_pause_reason ? (
        <AutopausedBadge
          reason={deliverable.auto_pause_reason}
          {...(onUnpause ? { onUnpause } : {})}
          isUnpausing={Boolean(isUnpausing)}
        />
      ) : null}
      {!deliverable.auto_paused_at && deliverable.paused_at ? (
        <FounderPausedBadge
          reason={deliverable.pause_reason ?? ''}
          {...(onUnpause ? { onUnpause } : {})}
          isUnpausing={Boolean(isUnpausing)}
        />
      ) : null}
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>meta paid ad</Trans>
        </span>
        <code className="font-mono text-[10px] tracking-tight text-[var(--ink)]/60">
          {deliverable.id}
        </code>
        <span className="ml-auto font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55">
          {deliverable.optimization_goal.toLowerCase().replaceAll('_', ' ')}
        </span>
        {launched ? (
          <Tooltip
            label={
              isMockLaunch
                ? t`the ad was created on our side but nothing was pushed to meta. connect a meta ad account + page at /channels to launch for real.`
                : t`launched to meta as PAUSED — review + activate in ads manager.`
            }
          >
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] ${
                isMockLaunch
                  ? 'border border-[var(--ink)]/30 bg-[var(--paper)] text-[var(--ink)]/65'
                  : 'border border-[var(--ink)] bg-[var(--spell-soft)] text-[var(--ink)]'
              }`}
            >
              {isMockLaunch ? t`created · pending launch` : t`launched · paused`}
            </span>
          </Tooltip>
        ) : null}
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
          ariaLabel={collapsed ? t`show ad details` : t`collapse ad details`}
        />
      </div>
      <CollapsibleBlock open={!collapsed}>
        {hasImage && deliverable.image_url ? (
          <div className="mt-3 overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--paper)]">
            <img
              src={deliverable.image_url}
              alt={`ad creative for ${deliverable.id}`}
              className="aspect-square w-full object-cover"
              loading="lazy"
            />
          </div>
        ) : null}
        <p className="mt-3 whitespace-pre-wrap font-serif text-[15px] leading-[1.5] text-[var(--ink)]">
          {deliverable.hook}
        </p>
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            {'↘'} <Trans>image brief</Trans>
          </p>
          {onGenerateImage && !launched ? (
            <button
              type="button"
              onClick={() => void onGenerateImage()}
              disabled={isGeneratingImage}
              className="btn btn-ghost text-[11px] disabled:opacity-60"
              title={
                hasImage
                  ? 'regenerate the ad creative — overwrites the current one'
                  : "render the ad creative with mag's image gen"
              }
            >
              {isGeneratingImage ? (
                <Trans>rendering…</Trans>
              ) : hasImage ? (
                <Trans>regenerate image</Trans>
              ) : (
                <Trans>generate image</Trans>
              )}
              <span className="btn-arrow">→</span>
            </button>
          ) : null}
        </div>
        <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/75">
          {deliverable.image_brief}
        </p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          {'↘'} <Trans>audience</Trans>
        </p>
        <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/85">
          {deliverable.audience_brief}
        </p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          {'↘'} <Trans>budget · ${dailyBudgetUsd}/day</Trans>
          {deliverable.destination_url ? (
            <span className="ml-3">
              {'↘'} <Trans>click sends to</Trans>{' '}
              <a
                href={deliverable.destination_url}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-[var(--ink)]"
              >
                {hostnameOf(deliverable.destination_url)}
              </a>
            </span>
          ) : null}
        </p>
        <p className="mt-3 mag-voice text-[13px] leading-[1.5] text-[var(--ink)]/65">
          “{deliverable.why}”
        </p>
      </CollapsibleBlock>
      {launched && !isMockLaunch ? (
        <>
          {metrics ? <MetaPaidAdMetricsBlock metrics={metrics} /> : null}
          {trendCampaignId ? (
            <MetaPaidAdTrendBlock campaignId={trendCampaignId} deliverableId={deliverable.id} />
          ) : null}
          <div className="mt-3 flex items-center justify-between gap-3 border-t border-[var(--line)] pt-3">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                ↘ {deliverable.launched_campaign_id}
              </span>
              <span className="font-mono text-[10px] text-[var(--ink)]/45">
                ·{' '}
                {deliverable.launched_at
                  ? formatDateTime(deliverable.launched_at, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : ''}
              </span>
            </div>
            {onRefreshMetrics ? (
              <button
                type="button"
                onClick={() => void onRefreshMetrics()}
                disabled={isRefreshingMetrics}
                className="btn btn-ghost text-[11px] disabled:opacity-60"
                title={metrics ? t`ask meta for fresh numbers` : t`ask meta how this ad's doing`}
              >
                {isRefreshingMetrics
                  ? t`asking meta…`
                  : metrics
                    ? t`refresh metrics`
                    : t`load metrics`}
                <span className="btn-arrow">→</span>
              </button>
            ) : null}
            {onPause && !deliverable.auto_paused_at && !deliverable.paused_at ? (
              <PauseAdButton onPause={onPause} isPausing={Boolean(isPausing)} />
            ) : null}
          </div>
        </>
      ) : onLaunch ? (
        <div className="mt-3 flex items-center justify-end gap-3 border-t border-[var(--line)] pt-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            {isMockLaunch
              ? t`↘ the ad is created on our side — push to meta now to go live (ships PAUSED)`
              : t`↘ ships PAUSED — you'll approve in ads manager before any spend`}
          </span>
          <button
            type="button"
            onClick={() => void onLaunch()}
            disabled={isLaunching || !hasImage}
            className="btn disabled:opacity-60"
            title={
              !hasImage
                ? t`generate the ad creative first — meta needs an image url to upload`
                : undefined
            }
          >
            {isLaunching ? t`launching…` : isMockLaunch ? t`push to meta` : t`launch on meta`}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      ) : (
        <div className="mt-3 border-t border-[var(--line)] pt-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            {isMockLaunch
              ? t`↘ connect a meta ad account at /channels to push this ad live`
              : t`↘ launch unlocks once you ship the campaign`}
          </p>
        </div>
      )}
    </article>
  );
}

/** Compact metrics block under a launched meta_paid_ad tile (s194).
 *  Six stats in a 3-col grid: impressions, clicks, ctr, spend, cpm,
 *  reach. "as of" timestamp + status badge run along the top so the
 *  founder can tell at a glance how fresh the numbers are and whether
 *  the ad is actually delivering. */
function MetaPaidAdMetricsBlock({
  metrics,
}: {
  metrics: NonNullable<MetaPaidAdDeliverable['metrics']>;
}) {
  const { t } = useLingui();
  const fetchedRelative = formatFetchedAt(metrics.fetched_at);
  const isMockMetrics = metrics.fetched_via === 'mock';
  return (
    <div className="mt-3 rounded-lg border border-[var(--line)] bg-[var(--paper)]/40 p-3">
      <div className="flex items-baseline gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>performance</Trans>
        </p>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] ${
            metrics.status === 'ACTIVE'
              ? 'border border-[var(--ink)] bg-[var(--spell-soft)] text-[var(--ink)]'
              : 'border border-[var(--ink)]/30 bg-[var(--paper)] text-[var(--ink)]/65'
          }`}
          title={
            metrics.status === 'ACTIVE'
              ? 'this ad is live on meta and spending.'
              : 'paused / not actively delivering — activate in ads manager when ready.'
          }
        >
          {metrics.status.toLowerCase()}
        </span>
        <span className="ml-auto font-mono text-[10px] text-[var(--ink)]/55">
          <Trans>as of {fetchedRelative}</Trans>
          {isMockMetrics ? <Trans> · mock</Trans> : null}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-x-3 gap-y-2">
        <MetaPaidAdMetric label={t`impressions`} value={formatNumber(metrics.impressions)} />
        <MetaPaidAdMetric label={t`link clicks`} value={formatNumber(metrics.clicks)} />
        <MetaPaidAdMetric label={t`ctr`} value={`${metrics.ctr_percent.toFixed(2)}%`} />
        <MetaPaidAdMetric label={t`spend`} value={`$${metrics.spend_usd.toFixed(2)}`} />
        <MetaPaidAdMetric label={t`cpm`} value={`$${metrics.cpm_usd.toFixed(2)}`} />
        <MetaPaidAdMetric label={t`reach`} value={formatNumber(metrics.reach)} />
      </div>
    </div>
  );
}

function MetaPaidAdMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {label}
      </p>
      <p className="mt-0.5 font-display text-[18px] font-extrabold leading-none tracking-[-0.02em] text-[var(--ink)]">
        {value}
      </p>
    </div>
  );
}

/** s197 — sparkline strip showing the last N CTR + spend snapshots
 *  for one launched paid ad. Renders nothing when there's only one
 *  snapshot (no trend to show yet) or while loading. The chart is
 *  intentionally minimal — three thin sparklines + a tooltip on
 *  hover — because the founder reads the latest values in the
 *  block above; this block is just "is the trend up/down/flat?". */
function MetaPaidAdTrendBlock({
  campaignId,
  deliverableId,
}: {
  campaignId: string;
  deliverableId: string;
}) {
  const { t } = useLingui();

  const historyQ = useQuery({
    queryKey: ['meta-paid-ad-metrics-history', campaignId, deliverableId],
    queryFn: () => fetchMetaPaidAdMetricsHistory(campaignId, deliverableId),
    // Stale fast — every time the founder hits refresh-metrics, a
    // new row appends. We don't want to show a stale curve.
    staleTime: 0,
    retry: false,
  });
  const history = historyQ.data ?? [];
  // Reverse to chronological (oldest → newest) for the sparkline.
  const series = [...history].reverse();
  if (series.length < 2) return null;

  const ctrValues = series.map((p) => p.ctrPercent);
  const spendValues = series.map((p) => p.spendUsd);
  const cpmValues = series.map((p) => p.cpmUsd);
  const firstFetched = series[0]?.fetchedAt;
  const lastFetched = series[series.length - 1]?.fetchedAt;

  return (
    <div className="mt-2 rounded-lg border border-[var(--line)] bg-[var(--paper)]/40 p-3">
      <div className="flex items-baseline gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} {plural(series.length, { one: 'trend · # read', other: 'trend · # reads' })}
        </p>
        {firstFetched ? (
          <span className="ml-auto font-mono text-[10px] text-[var(--ink)]/55">
            {formatTrendRange(firstFetched, lastFetched ?? firstFetched)}
          </span>
        ) : null}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <Sparkline label={t`ctr`} values={ctrValues} unit="%" />
        <Sparkline label={t`spend`} values={spendValues} unit="$" />
        <Sparkline label={t`cpm`} values={cpmValues} unit="$" />
      </div>
    </div>
  );
}

/** Minimal inline sparkline using a viewBox-normalized SVG polyline.
 *  No charting library — these are 60-px-wide strips. The latest
 *  value + the delta vs the first point sit beside the line as the
 *  data-cue; the line itself is decoration. */
function Sparkline({ label, values, unit }: { label: string; values: number[]; unit: '%' | '$' }) {
  const first = values[0] ?? 0;
  const last = values[values.length - 1] ?? 0;
  const delta = last - first;
  const deltaPct = first > 0 ? (delta / first) * 100 : 0;
  const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';

  const width = 80;
  const height = 22;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1 || 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const formatted = unit === '$' ? `$${last.toFixed(2)}` : `${last.toFixed(2)}%`;
  const deltaLabel =
    direction === 'flat'
      ? '·'
      : `${direction === 'up' ? '↗' : '↘'} ${Math.abs(deltaPct).toFixed(0)}%`;

  return (
    <div>
      <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {label}
      </p>
      <div className="mt-1 flex items-end gap-2">
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={`${label} sparkline`}
          className="overflow-visible"
        >
          <polyline
            points={points}
            fill="none"
            stroke="var(--ink)"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
        <span className="font-display text-[14px] font-extrabold leading-none tracking-[-0.02em] text-[var(--ink)]">
          {formatted}
        </span>
      </div>
      <p className="mt-1 font-mono text-[9.5px] text-[var(--ink)]/55">{deltaLabel}</p>
    </div>
  );
}

function formatTrendRange(firstIso: string, lastIso: string): string {
  const first = new Date(firstIso);
  const last = new Date(lastIso);
  const sameDay = first.toDateString() === last.toDateString();
  if (sameDay) {
    return `${formatTime(first, { hour: 'numeric', minute: '2-digit' })} → ${formatTime(last, { hour: 'numeric', minute: '2-digit' })}`;
  }
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${formatDate(first, opts)} → ${formatDate(last, opts)}`;
}

/** Wave 2's helper replaces what this hand-rolled: "just now" / "5m ago"
 *  / "3h ago" were English assembled in a .ts helper, which no linter
 *  here can see — there is no Intl call to grep for and no JSX to flag.
 *  Intl.RelativeTimeFormat emits the localized narrow form directly, so
 *  none of those three ever needs to be a catalog entry. Beyond a day it
 *  still falls back to a date, which reads better than "27d ago". */
function formatFetchedAt(iso: string): string {
  const fetched = new Date(iso);
  const diffSec = (Date.now() - fetched.getTime()) / 1000;
  if (diffSec < 60 * 60 * 24) return formatRelativeTimeCompact(fetched);
  return formatDate(fetched, { month: 'short', day: 'numeric' });
}

function OptimizingPanel({ optimizingFor }: { optimizingFor: string }) {
  const { t } = useLingui();
  return (
    <Panel eyebrow={t`optimizing for`}>
      <p className="text-[15px] leading-snug text-[var(--ink)]">{optimizingFor}</p>
      <p className="mt-2 text-[12px] leading-[1.45] text-[var(--ink)]/65">
        <Trans>
          meta + google both report this — i'll bias toward whichever platform converts cheaper
          after the first 48 hours.
        </Trans>
      </p>
    </Panel>
  );
}

function TimelinePanel({ timeline }: { timeline: string }) {
  const { t } = useLingui();
  return (
    <Panel eyebrow={t`timeline`}>
      <p className="text-[15px] leading-snug text-[var(--ink)]">{timeline}</p>
      <p className="mt-2 text-[12px] leading-[1.45] text-[var(--ink)]/65">
        <Trans>first 48 hours are noise — i'll wait until friday before calling anything.</Trans>
      </p>
    </Panel>
  );
}

/** s181 — mag's self-flagged uncertainty. Lives above the action row
 *  so the founder reads mag's caveats BEFORE approving. Empty array
 *  is treated as "mag was confident across the board" and we render
 *  a quiet note instead of hiding the block — keeps the founder
 *  trained to look for it. */
function HonestConcernsBlock({ concerns }: { concerns: string[] }) {
  const concernCount = concerns.length;
  if (concerns.length === 0) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-[var(--ink)]/15 bg-[var(--paper)]/40 p-4">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/45">
          {'↘'} <Trans>mag's caveats</Trans>
        </p>
        <p className="mt-1.5 text-[12.5px] leading-[1.45] text-[var(--ink)]/55">
          <Trans>
            nothing flagged — mag is confident on this one. push back in chat after launch if you
            spot something.
          </Trans>
        </p>
      </div>
    );
  }
  return (
    <div className="mt-6 rounded-2xl border-[1.5px] border-[var(--plum)]/40 bg-[var(--spell-soft)]/50 p-6">
      <div className="flex items-start gap-3">
        <Mag size={36} mood="default" />
        <div className="flex-1">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>mag's caveats · {concernCount}</Trans>
          </p>
          <p className="mt-1 font-display text-[18px] font-semibold leading-tight tracking-[-0.01em] text-[var(--ink)]">
            <Trans>things i want to flag before we ship.</Trans>
          </p>
          <p className="mt-1 text-[12.5px] leading-[1.45] text-[var(--ink)]/60">
            <Trans>
              i'll callback to these later if results come in pointing at one. read them now, push
              back in chat if you disagree.
            </Trans>
          </p>
        </div>
      </div>
      <ul className="mt-5 flex flex-col gap-3">
        {concerns.map((c, i) => (
          <li
            key={c}
            className="relative flex gap-4 overflow-hidden rounded-xl border border-[var(--line)] bg-white p-4 shadow-[0_1px_0_var(--line)]"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-[var(--plum)]/70"
            />
            <span className="shrink-0 self-start font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]/70 tabular-nums">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="mag-voice text-[15px] leading-[1.55] text-[var(--ink)]/90">{c}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Launch block for plans WITHOUT a oneAsk. Mag didn't surface a
 *  fork — the founder just approves.
 *
 *  s182 — the copy + the choice slug now depend on what's actually
 *  in the plan. Analytics-event-heavy plans phrase the action as
 *  "i've installed these"; creative-only plans phrase it as "ship
 *  it". Same wire shape (choice string passed to onChoice) so the
 *  launchCampaign endpoint stays unchanged. */
function PlainLaunchBlock({
  working,
  onChoice,
  campaignName,
  analyticsEventCount,
  hasPaidHooks,
}: {
  working: string | null;
  onChoice: (choice: string) => void | Promise<void>;
  campaignName: string;
  analyticsEventCount: number;
  hasPaidHooks: boolean;
}) {
  const { t } = useLingui();
  // Pick the action copy based on the dominant deliverable kind.
  // Analytics-heavy → "i've installed these" (matches the founder's
  // mental model of "i did the snippet work, mag can start watching").
  // Creative-only or mixed → "approve plan" (this just locks in the
  // plan + creates ads on our side — each ad still has a per-platform
  // launch click on the campaign page so the trust ladder can earn
  // its way to autopilot). Earlier copy was "ship it" which read as
  // "this clicks live now" — confusing when the founder then saw a
  // per-ad "launch on google" button on the next page.
  const analyticsOnly = analyticsEventCount > 0 && !hasPaidHooks;
  const eyebrow = analyticsOnly ? 'one thing i need you to decide' : 'ready when you are';
  const subhead = analyticsOnly
    ? `${analyticsEventCount} snippets to paste. tell me when it's done and i'll start watching.`
    : "no forks here — read the plan, the caveats above, and approve when ready. i'll create each ad and you'll launch them one click each from the campaign page.";
  const buttonLabel = analyticsOnly ? "i've installed these" : 'approve plan';
  const choiceSlug = analyticsOnly ? 'installed' : 'launch';
  return (
    <div className="mt-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-[var(--spell)] p-5 shadow-[6px_6px_0_var(--ink)]">
      <div className="flex items-start gap-3">
        <Mag size={36} />
        <div className="flex-1">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/65">
            ↘ {eyebrow}
          </p>
          <p className="mt-2 font-serif text-[20px] leading-tight text-[var(--ink)]">“{subhead}”</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            {'↘'} <Trans>campaign · {campaignName}</Trans>
          </p>
          <div className="mt-4 flex items-center justify-end">
            <button
              type="button"
              onClick={() => onChoice(choiceSlug)}
              disabled={working !== null}
              className="btn disabled:opacity-60"
            >
              {working === choiceSlug ? t`shipping…` : buttonLabel}
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OneAskBlock({
  oneAsk,
  working,
  onChoice,
  campaignName,
}: {
  oneAsk: NonNullable<CampaignPlan['oneAsk']>;
  working: string | null;
  onChoice: (choice: string) => void | Promise<void>;
  campaignName: string;
}) {
  const { t } = useLingui();
  const ask = oneAsk;
  return (
    <div className="mt-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-[var(--spell)] p-5 shadow-[6px_6px_0_var(--ink)]">
      <div className="flex items-start gap-3">
        <Mag size={36} />
        <div className="flex-1">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/65">
            ↘ {ask.eyebrow}
          </p>
          <p className="mt-2 font-serif text-[22px] leading-tight text-[var(--ink)]">
            {ask.question}
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            {'↘'} <Trans>campaign · {campaignName}</Trans>
          </p>
          {/* THESE ARE ANSWERS, NOT ACTIONS, and they were styled as
              actions. mag writes a full sentence per option — "confirmation
              page after registration (paste the url)" — and a row of
              right-aligned auto-width buttons truncated them mid-word and
              stacked ragged, so the founder was choosing between two
              half-sentences.
              Full-width rows, each the same size, label wrapping instead of
              clipping, and the affordance still at the right edge. */}
          <div className="mt-4 flex flex-col gap-2">
            {ask.options.map((opt, i) => (
              <button
                key={opt.choice}
                type="button"
                onClick={() => onChoice(opt.choice)}
                disabled={working !== null}
                className={`btn ${i === 0 ? '' : 'btn-ghost'} w-full justify-between gap-3 text-left disabled:opacity-60`}
              >
                <span className="whitespace-normal leading-snug">
                  {working === opt.choice ? t`shipping…` : opt.label}
                </span>
                <span className="btn-arrow shrink-0">→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Autopilot A3b-finish — paused-by-mag banner rendered above paid
 *  ad tiles when deliverable.auto_paused_at is set. Founder hits
 *  "resume" to clear the autopause + re-enable the campaign on
 *  Meta/Google's side. */
function AutopausedBadge({
  reason,
  onUnpause,
  isUnpausing,
}: {
  reason: string;
  onUnpause?: () => void | Promise<void>;
  isUnpausing: boolean;
}) {
  return (
    <div className="mb-3 rounded-md border border-[var(--pulse)]/40 bg-[var(--pulse)]/5 px-3 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--pulse)]">
          {'↘'} <Trans>paused by mag</Trans>
        </p>
        {onUnpause ? (
          <button
            type="button"
            onClick={onUnpause}
            disabled={isUnpausing}
            className="self-end rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isUnpausing ? <Trans>resuming…</Trans> : <Trans>resume</Trans>}
          </button>
        ) : null}
      </div>
      <p className="mt-1 font-serif text-[12px] leading-[1.5] text-[var(--ink)]/75">“{reason}”</p>
    </div>
  );
}

/** The founder's OWN pause, which is a different sentence from mag's.
 *
 *  Keyed on `paused_at`, which AutopausedBadge above does not read — it
 *  keys on `auto_paused_at`. Until this existed a deliverable the
 *  founder paused (through mag in chat, the only pause path there was)
 *  rendered as live: the fields were stamped and nothing showed them.
 *  Resume goes through the same endpoint as mag's pause, because
 *  resumeCampaignDeliverable already clears both sets of fields. */
function FounderPausedBadge({
  reason,
  onUnpause,
  isUnpausing,
}: {
  reason: string;
  onUnpause?: () => void | Promise<void>;
  isUnpausing: boolean;
}) {
  return (
    <div className="mb-3 rounded-md border border-[var(--ink)]/25 bg-[var(--paper)] px-3 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/70">
          {'\u2016'} <Trans>paused by you</Trans>
        </p>
        {onUnpause ? (
          <button
            type="button"
            onClick={onUnpause}
            disabled={isUnpausing}
            className="self-end rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/75 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isUnpausing ? <Trans>resuming…</Trans> : <Trans>resume</Trans>}
          </button>
        ) : null}
      </div>
      <p className="mt-1 font-serif text-[12px] leading-[1.5] text-[var(--ink)]/75">“{reason}”</p>
    </div>
  );
}

/** Pause this ad, from the page the founder is already looking at.
 *
 *  Rendered only on a LAUNCHED, not-already-paused paid tile — there is
 *  nothing to pause before that, and the two badges above own the
 *  already-paused states. Before this the api could unpause something
 *  the founder had no way to pause: the only founder-initiated pause
 *  was asking mag in chat. */
function PauseAdButton({
  onPause,
  isPausing,
}: {
  onPause: () => void | Promise<void>;
  isPausing: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onPause}
      disabled={isPausing}
      className="rounded-md border border-[var(--ink)]/25 bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/70 transition hover:border-[var(--ink)]/45 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isPausing ? <Trans>pausing…</Trans> : <Trans>pause this ad</Trans>}
    </button>
  );
}

/** Autopilot A6-finish — "mag drafted this · review before
 *  launch" banner rendered above google_search_ad tiles when
 *  deliverable.autopilot_drafted_at is set. Founder hits
 *  "approve" to clear the field; A4 then auto-launches on the
 *  next tick. */
function AutopilotDraftedBadge({
  onApprove,
  isApproving,
}: {
  onApprove?: () => void | Promise<void>;
  isApproving: boolean;
}) {
  const { t } = useLingui();
  return (
    <div className="mb-3 rounded-md border border-[var(--plum)]/40 bg-[var(--spell-soft)]/60 px-3 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
          {'↘'} <Trans>i drafted this · review before launch</Trans>
        </p>
        {onApprove ? (
          <button
            type="button"
            onClick={onApprove}
            disabled={isApproving}
            className="self-end rounded-md border border-[var(--ink)] bg-[var(--spell)] px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink)] transition hover:bg-[var(--spell-soft)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isApproving ? t`approving…` : t`approve · launch`}
          </button>
        ) : null}
      </div>
      <p className="mt-1 font-serif text-[12px] leading-[1.5] text-[var(--ink)]/75">
        <Trans>
          i autodrafted this from your strategy. read it, then click approve to ship — i'll launch
          it within the next tick.
        </Trans>
      </p>
    </div>
  );
}

// ─── Plan-mode trail ──────────────────────────────────────────────
// Rendered above PlanReviewBody when the campaign was drafted via the
// multi-agent panel. The synthesis note is mag's "why this plan" intro;
// the critique trail is the show-your-work expansion.

function PanelSynthesisCard({ log }: { log: CampaignPlanningLog }) {
  return (
    // THE CARD IS SIZED TO THE PROSE, not to the column. `.mag-md--longform`
    // caps the measure at 68ch because mag writes four or five real
    // paragraphs here and the full card width ran ~110 characters a line
    // (see the MEASURE note in styles/index.css). The cap was right; the card
    // was never narrowed to match, so the leftover width piled up as a dead
    // gutter on one side and read as a layout bug. Capping the CARD at the
    // same measure plus its padding keeps the line length and loses the
    // gutter — removing the cap instead would bring the 110-char lines back.
    <div className="max-w-[calc(68ch+2.5rem)] rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="mb-3 flex items-center gap-2.5">
        <Mag size={22} mood="default" />
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
          <Trans>mag · why this plan</Trans>
        </span>
      </div>
      <div className="text-[15px] leading-relaxed text-[var(--ink)]">
        {/* [s376] The one long-form surface: mag writes four or five
            paragraphs here, and the chat-tight rhythm read as one wall
            of text. */}
        <MagMessageBody body={log.synthesisNote} tone="light" density="longform" />
      </div>
    </div>
  );
}
